import React, { useEffect, useRef, useState } from 'react';
import Phaser from 'phaser';
import { MultiplayerManager } from './MultiplayerManager.js';
import QuizOverlay from '../../LearningWorld/components/QuizOverlay';
import useMusicManager from '../../../hooks/useMusicManager';
import MusicControlModal from '../../../components/MusicControlModal';

class GameScene extends Phaser.Scene {
  constructor() {
    super({ key: 'GameScene' });
    this.energy = 100;
    this.maxEnergy = 100;
    this.jumpEnergyCost = 5;
    this.isGameOver = false;
    this.score = 0;
    this.highestPlatform = 0;
    this.cameraFollowPlayer = true;
    this.jumpsRemaining = 2; // Allow double jump
    this.maxJumps = 3;
    this.wasOnGround = false; // Track ground state
    this.jumpKeyPressed = false;
    this.currentPlatform = null; // Track which platform frog is on for moving platform physics
    this.levelTransitioning = false; // Guard against double next-level triggers

    // Infinite generation properties
    this.highestGeneratedY = 600; // Start at ground level
    this.generationChunkSize = 200; // Generate platforms in chunks of 200 units
    this.currentLevel = 0; // Current difficulty level (every 100m)
    this.platformCleanupDistance = 2000; // Keep more platforms around for falling - was 800
    this.playerType = 'rectangle'; // Default player type

    // Background properties
    this.currentBackgroundTheme = 0;
    this.backgroundLayers = [];

    // Multiplayer properties
    this.multiplayerManager = null;
    this.isMultiplayerEnabled = true;

    // Server-side level properties
    this.currentServerLevel = 1;
    this.serverLevelData = null;
    this.userStartingLevel = 1;
    this.loadingLevelData = false;

    // Progress tracking
    this.totalJumps = 0;
    this.totalEnergyCollected = 0;
    this.totalGemsCollected = 0;
    this.totalCoinsCollected = 0;
    this.levelCompleted = false;

    // Quiz system tracking
    this.showingQuiz = false;
  }

  preload() {
    // Load the frog sprite sheet from public directory
    this.load.atlas('frog', '/frog_sprite.png', '/frog_sprite.json');

    // Load the gem image
    this.load.image('gem', '/gem.png');

    // Load the coin image
    this.load.image('coin', '/coin.png');

    // Load the block sprite for Mario-style platforms
    this.load.image('block', '/block-sprite.png');
  }

  create() {
    // Initialize level completion flag
    this.levelCompleting = false;

    // Ensure mobile flag exists even if React hasn't injected it yet
    if (typeof this.isMobile === 'undefined') {
      const ua = navigator.userAgent || '';
      this.isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua) || window.innerWidth <= 1200;
    }

    // Set MUCH larger world bounds for infinite vertical platforming
    // Always use full game width (1200px) for physics world so mobile can access all platforms
    const fullGameWidth = 1200;
    this.physics.world.setBounds(0, -10000, fullGameWidth, 12000);

    // Store fullGameWidth for use throughout the scene
    this.fullGameWidth = fullGameWidth;

    // Create dynamic backgrounds
    this.createBackgroundSystem();

    // Create platforms group
    this.platforms = this.physics.add.staticGroup();
    this.energyOrbs = this.physics.add.group();
    this.coins = this.physics.add.group();

    // Ground will be created as part of level generation

    // Load user's progress and start at appropriate level
    this.loadUserJavaScriptProgress();

    // Create player - try frog sprite first, fallback to rectangle
    const hasFrogAtlas = this.textures.exists('frog');

    if (hasFrogAtlas) {
      try {
        // Create player as animated frog sprite
        this.player = this.add.sprite(200, 550, 'frog', 'frog_grid_0');
        this.player.setScale(0.15); // Scale down from 384x1024 to ~38x102 pixels
        this.player.setOrigin(0.5, 1.2); // Center horizontally, slightly above bottom to sit on platforms
        this.physics.add.existing(this.player);
        this.player.body.setSize(35, 0); // Set physics body size to match sprite

        // Make sure frog has proper collision settings
        this.player.body.setMaxVelocity(300, 1000); // Prevent going too fast horizontally
        this.player.body.setBounce(0.15);
        this.player.body.setGravityY(600);

        // Create frog animations
        this.anims.create({
          key: 'frog_idle',
          frames: [
            { key: 'frog', frame: 'frog_grid_0' },
            { key: 'frog', frame: 'frog_grid_1' }
          ],
          frameRate: 2,
          repeat: -1
        });

        this.anims.create({
          key: 'frog_jump',
          frames: [
            { key: 'frog', frame: 'frog_grid_2' }
          ],
          frameRate: 8,
          repeat: 0
        });

        // Start with idle animation
        this.player.play('frog_idle');

        // Make sure frog appears above platforms
        this.player.setDepth(10);

        this.playerType = 'frog';

      } catch (error) {
        this.createFallbackPlayer();
      }
    } else {
      this.createFallbackPlayer();
    }

    // Player collides with platforms - make sure collision is solid
    this.platformCollider = this.physics.add.collider(this.player, this.platforms, this.playerLanded, null, this);

    // ALSO add overlap detection to catch landings that collision might miss
    this.physics.add.overlap(this.player, this.platforms, this.playerLandedOverlap, null, this);

    this.physics.add.overlap(this.player, this.energyOrbs, this.collectEnergyOrb, null, this);
    this.physics.add.overlap(this.player, this.coins, this.collectCoin, null, this);

    // Only collide with left, right, and bottom world bounds - not top
    this.player.body.setCollideWorldBounds(true);
    this.physics.world.setBoundsCollision(true, true, false, true); // left, right, up, down

    // Input handling
    this.cursors = this.input.keyboard.createCursorKeys();
    this.wasd = this.input.keyboard.addKeys('W,S,A,D,SPACE');

    // Camera follows player with some smoothing
    this.cameras.main.startFollow(this.player, true, 0.1, 0.1);
    this.cameras.main.setDeadzone(this.fullGameWidth * 0.15, 80);

    // Game over text (keep this one for game over screen)
    this.gameOverText = this.add.text(this.fullGameWidth / 2, 300, '', {
      fontSize: '32px',
      fill: '#ff0000',
      backgroundColor: 'transparent', // Start transparent, will be set to black when needed
      padding: { x: 0, y: 0 }         // No padding when empty
    }).setOrigin(0.5).setScrollFactor(0);

    // Stats moved to React nav bar - no more in-game UI text

    // Initialize React stats
    setTimeout(() => this.updateReactStats(), 200);

    // Initialize multiplayer
    this.initializeMultiplayer();
  }

  initializeMultiplayer() {
    if (this.isMultiplayerEnabled) {
      this.multiplayerManager = new MultiplayerManager(this);
      this.multiplayerManager.connect();
    }
  }

  // DISABLED - Load user progress and level data from server
  async loadUserProgressAndLevel() {
    // Do nothing - we're using JavaScript-only generation
    this.loadingLevelData = false;
  }

  // Load specific level data from server
  async loadServerLevel(levelNumber) {
    try {
      const response = await fetch(`/api/jump_game/levels/${levelNumber}`, {
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Content-Type': 'application/json'
        },
        credentials: 'include' // Include cookies for authentication
      });
      const levelData = await response.json();

      if (levelData.success) {
        this.currentServerLevel = levelNumber;
        this.serverLevelData = levelData.data;

        // Generate platforms from server data
        this.generatePlatformsFromServerData(levelData.data);

        // Update player position to appropriate level
        if (this.player && levelNumber > 1) {
          this.movePlayerToStartingLevel(levelNumber);
        }

        // (removed target height storage - level completion is now goal platform only)

      } else {
        throw new Error(levelData.error || 'Failed to load level');
      }
    } catch (error) {
      console.error(`❌ Failed to load level ${levelNumber}:`, error);
      console.error('🚨 API call failed - using fallback generation');
      // Fallback to random generation
      this.generateInitialPlatforms();
    }
  }

  // Generate platforms from server-provided data
  generatePlatformsFromServerData(levelData) {
    // Do nothing - we're using JavaScript-only generation
  }

  // DISABLED - Create platform from server data
  createServerPlatform(platformData) {
    // Do nothing - we're using JavaScript-only generation
    return null;
  }

  // OLD createServerPlatform method (disabled)
  createServerPlatform_OLD(platformData) {
    const { x, y, type, size, is_moving, move_speed, move_range } = platformData;

    // Create the platform sprite
    let platform;
    // Calculate platform size based on data
    const baseWidth = 120; // Base platform width
    const platformWidth = baseWidth * (size || 1);
    const platformHeight = 32;

    // Different types for different platforms
    const isGround = platformData.id && platformData.id.includes('ground_platform');
    const isGoal = platformData.id && platformData.id.includes('goal_platform');

    // Use clean colored rectangles for now - reliable and simple
    let color;
    if (isGround) {
      color = 0x8B4513; // Brown for ground
    } else if (isGoal) {
      color = 0xFFD700; // Gold for goal platform
    } else {
      color = 0xFF8C00; // Orange for regular blocks
    }

    // Create platform as styled rectangle with border
    platform = this.add.rectangle(x, y, platformWidth, platformHeight, color);
    platform.setStrokeStyle(2, 0x654321); // Dark brown border
    platform.setDepth(1); // Ensure platforms are above background

    // Add to platforms group for physics
    this.platforms.add(platform);

    // Ensure body exists and configure it
    if (platform.body) {
      // Set collision body to exact size for predictable physics
      platform.body.setSize(platformWidth, platformHeight);
      platform.body.setOffset(0, 0); // No offset - body matches visual exactly

      // Enable all collisions for proper physics
      platform.body.checkCollision.up = true;
      platform.body.checkCollision.down = true;
      platform.body.checkCollision.left = true;
      platform.body.checkCollision.right = true;
    } else {
      console.error('❌ Platform body not created properly for platform at:', x, y);
    }

    // Store platform data
    platform.platformData = platformData;
    platform.platformY = y; // For cleanup

    // No moving platforms for now - keeping gameplay simple and reliable
    if (false && is_moving && move_speed && move_range) {
      platform.originalX = x;
      platform.moveSpeed = move_speed / 3; // Slower movement
      platform.moveRange = move_range;
      platform.moveDirection = 1;
      platform.isMovingPlatform = true;
      platform.playerOnPlatform = false; // Track if player is on this platform

      // Moving platforms need special physics
      platform.body.setImmovable(true);
      platform.body.setVelocityX(platform.moveSpeed);
    }

    // No need for decorative tiles - we're using the main platform sprite

    // Add to platforms group
    this.platforms.add(platform);

    return platform;
  }

  // Create energy orb from server data
  createServerOrb(orbData) {
    const { x, y, energy_value } = orbData;

    let orb;
    const hasGemImage = this.textures.exists('gem');

    if (hasGemImage) {
      orb = this.add.image(x, y, 'gem');
      orb.setScale(0.3);
    } else {
      // Fallback to circle
      orb = this.add.circle(x, y, 8, 0xFFD700);
    }

    // Add physics
    this.physics.add.existing(orb);
    orb.body.setSize(orb.width * 0.8, orb.height * 0.8);

    // Store orb data
    orb.energyValue = energy_value || 10;
    orb.orbY = y; // For cleanup

    // Add to orbs group
    this.energyOrbs.add(orb);

    return orb;
  }

  // Move player to appropriate starting position for their level
  movePlayerToStartingLevel(level) {
    if (!this.player || !this.serverLevelData) return;

    // For continuing players, spawn them on the ground floor of their level
    // Find a ground platform to spawn on
    const platforms = this.serverLevelData.platforms || [];
    const groundPlatforms = platforms.filter(p => p.id && p.id.includes('ground_platform'));

    if (groundPlatforms.length > 0) {
      // Use the first ground platform as spawn point
      const spawnPlatform = groundPlatforms[0];
      this.player.x = spawnPlatform.x;
      this.player.y = spawnPlatform.y - 50; // Spawn above the platform

    } else {
      // Fallback to center of screen at ground level
      const displayWidth = this.sys.game.config.width;
      this.player.x = displayWidth / 2;
      this.player.y = 530; // Just above ground level
    }
  }

  generateLevel(levelNumber) {
    // DISABLED - OLD SERVER METHOD - Redirect to JavaScript generation
    this.generateJavaScriptLevel(levelNumber);
  }

  generateInitialPlatforms() {
    // Fallback - just generate level 1 with JavaScript
    this.generateJavaScriptLevel(1);
  }

  generateJavaScriptLevel(levelNumber) {

    // Safety: remove any leftover completion UI
    if (this.levelCompleteUI && Array.isArray(this.levelCompleteUI)) {
      try {
        this.levelCompleteUI.forEach(el => el && el.destroy && el.destroy());
      } catch (e) {
        console.warn('⚠️ Error cleaning leftover completion UI:', e);
      }
      this.levelCompleteUI = null;
    }

    // Fully clear prior level objects (platforms, tiles, orbs)
    this.clearLevelObjects();

    // Use full game width for platform placement so mobile can reach all platforms
    const gameWidth = 1200;

    // Level parameters - 1000m tall levels with properly spaced platforms
    const platformCount = Math.min(60 + (levelNumber * 3), 80); // Good number of platforms (60-80)
    const levelHeight = 10000; // 1000m = 10,000px (much taller!)
    const groundY = 580; // Ground platform position

    // Create ground platform first - spans full game width for solid foundation
    this.createJavaScriptPlatform(gameWidth / 2, groundY, gameWidth, 32, 0x8B4513, 'ground');
    const goalY = groundY - levelHeight; // Goal at the very top

    // Proper vertical spacing - room for player to fit and jump
    const verticalSpacing = 150; // Platforms every 150px (15m) - room for player + jumping
    // Horizontal spawn within a centered play lane on desktop
    const playLaneWidth = this.isMobile ? gameWidth : Math.min(gameWidth, 900);
    const laneStartX = (gameWidth - playLaneWidth) / 2;
    const marginBase = this.isMobile ? 0.18 : 0.10;
    const marginPx = Math.max(this.isMobile ? 64 : 50, Math.min(this.isMobile ? 140 : 120, playLaneWidth * marginBase));

    // Generate platforms with proper spacing for realistic climbing
    for (let i = 0; i < platformCount; i++) {
      // Proper vertical spacing - platforms every 150px with room to move
      const y = groundY - (i * verticalSpacing) - 50; // Start 50px above ground

      // Spawn X within reachable central play lane
      const x = laneStartX + marginPx + Math.random() * (playLaneWidth - 2 * marginPx);

      // Create regular platform (width uses block size for 1-block pads)
      const baseBlock = this.isMobile ? 40 : 32;
      this.createJavaScriptPlatform(x, y, baseBlock, baseBlock, 0xFF8C00, 'regular'); // Orange

      // Add gems randomly - about 30% chance per platform
      if (Math.random() < 0.3) {
        this.createEnergyOrb(x, y - 40); // Place gem above platform
      }

      // Add floating coins randomly - about 20% chance per platform
      if (Math.random() < 0.2) {
        this.createCoin(x + (Math.random() * 40 - 20), y - 70);
      }

      const heightM = Math.round((groundY - y) / 10);
    }

    // Place goal platform above the highest regular platform
    const highestPlatformY = groundY - (platformCount * verticalSpacing) - 50;
    const reachableGoalY = highestPlatformY - 100; // Goal above highest platform

    const goalX = gameWidth / 2; // Center the goal platform
    const goalPlatform = this.createJavaScriptPlatform(goalX, reachableGoalY, 180, 32, 0xFFD700, 'goal'); // Gold, larger
    this.levelGoalY = reachableGoalY; // Set goal height for completion detection
    // NOW position player safely above the ground platform after all platforms are created
    this.player.setPosition(gameWidth / 2, groundY - 50); // Center of ground, safely above ground platform at y=580
    this.player.body.setVelocity(0, 0);

    // Safety check: ensure player is on a valid platform
    this.ensurePlayerOnValidPlatform();

    // Log level stats for debugging
    this.logLevelStats('js-level-load');

    // Ambient coins anywhere across the board
    this.generateAmbientCoins(levelNumber, groundY, reachableGoalY);
  }

  // Handle JavaScript level completion
  handleJavaScriptLevelComplete() {
    // Prevent multiple completions or transitions
    if (this.levelCompleting || this.levelTransitioning) {
      return;
    }

    this.levelCompleting = true;

    // Show React modal instead of in-game UI
    if (this.setShowLevelComplete) {
      this.setShowLevelComplete(this.currentLevel);
    }
  }

  // Called by React component to proceed to next level
  async proceedToNextLevel() {
    this.levelCompleting = false;
    this.levelTransitioning = true;
    // Save progress only (do not generate a new level here)
    const ok = await this.saveProgressOnly(this.currentLevel + 1);
    this.levelTransitioning = false;
    return ok;
  }

  // Load the next JavaScript level
  async loadNextJavaScriptLevel() {
    this.currentLevel += 1;
    // Save progress to server first and wait for it to complete
    await this.saveJavaScriptLevelProgress(this.currentLevel);
  }

  // Load user's JavaScript level progress from server
  async loadUserJavaScriptProgress() {
    // Load progress from server only (no more cookies)
    
    // For quiz mode, always start at level 1
    if (this.quizCode) {
      this.currentLevel = 1;
      this.loadServerLevel(this.currentLevel);
      return;
    }

    // Try to get level from server
    try {
      const response = await fetch('/api/jump_game/user_progress', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
        },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        this.currentLevel = data.highest_level || 1;
      } else {
        this.currentLevel = 1;
      }
    } catch (error) {
      this.currentLevel = 1;
    }


    this.loadServerLevel(this.currentLevel);
  }

  // Load level data from database API
  async loadServerLevel(levelNumber) {
    try {
      const response = await fetch(`/api/jump_game/levels/${levelNumber}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
        },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          this.createLevelFromServerData(data.data, levelNumber);
          return;
        }
      }
    } catch (error) {
      console.error(`Error loading server level ${levelNumber}:`, error);
    }

    // Fallback to JavaScript generation if API fails
    this.generateJavaScriptLevel(levelNumber);
  }

  // Create level from consistent server data
  createLevelFromServerData(levelData, levelNumber) {
    const gameWidth = this.sys.game.config.width;

    // Fully clear prior level objects (platforms, tiles, orbs)
    this.clearLevelObjects();

    // Find ground platforms to determine spawn position
    let groundY = 580; // default
    let hasGround = false;
    let minPlatformY = null; // for estimating goal height

    if (levelData.platforms && Array.isArray(levelData.platforms)) {
      const groundPlatformsFromData = levelData.platforms.filter(p => p.id && p.id.includes('ground'));
      if (groundPlatformsFromData.length > 0) {
        groundY = groundPlatformsFromData[0].y;
      }
    }

    // Create platforms from server data
    if (levelData.platforms && Array.isArray(levelData.platforms)) {
      levelData.platforms.forEach(platformData => {
        const x = platformData.x || 400;
        const y = platformData.y || 500;
        const width = (platformData.size || 1) * 120; // Convert size to width

        // Determine platform type based on server data
        let platformType = 'regular';
        let color = 0xFF8C00; // Orange default

        if (platformData.id && platformData.id.includes('ground')) {
          platformType = 'ground';
          color = 0x8B4513; // Brown
        } else if (platformData.id && platformData.id.includes('goal')) {
          platformType = 'goal';
          color = 0xFFD700; // Gold
        }

        // Track top-most platform Y
        if (minPlatformY === null || y < minPlatformY) {
          minPlatformY = y;
        }

        // Create platform using your existing method
        const platform = this.createJavaScriptPlatform(x, y, width, 32, color, platformType);
        if (platformType === 'ground') hasGround = true;
        // Handle moving platforms
        if (platformData.is_moving) {
          platform.isMoving = true;
          platform.moveSpeed = platformData.move_speed || 50;
          platform.moveRange = platformData.move_range || 100;
          platform.startX = x;
          platform.direction = 1;
        }
      });
    }

    // Ensure there is always a ground platform
    if (!hasGround) {
      console.warn('⚠️ No ground platform found in server data. Creating default ground.');
      this.createJavaScriptPlatform(gameWidth / 2, groundY, gameWidth, 32, 0x8B4513, 'ground');
      hasGround = true;
    }

    // Create orbs/collectibles from server data
    if (levelData.orbs && Array.isArray(levelData.orbs)) {
      levelData.orbs.forEach(orbData => {
        // Use the same floating orb creation as JS levels
        this.createEnergyOrb(orbData.x, orbData.y);
      });
    }

    // Reset player position above ground after platforms exist
    this.player.setPosition(gameWidth / 2, groundY - 50);
    this.player.body.setVelocity(0, 0);

    // Safety: ensure player is on a valid platform
    this.ensurePlayerOnValidPlatform();

    // Ambient coins for server levels too
    const estimatedGoalY = (typeof minPlatformY === 'number') ? (minPlatformY - 200) : (groundY - 4000);
    this.generateAmbientCoins(levelNumber, groundY, estimatedGoalY);

    // Log level stats for debugging
    this.logLevelStats('server-level-load');
  }

  logLevelStats(context = '') {
    try {
      const totalPlatforms = this.platforms?.children?.entries?.length || 0;
      const groundCount = this.platforms?.children?.entries?.filter(p => p.isGroundPlatform)?.length || 0;
      const playerPos = this.player ? `${this.player.x.toFixed(1)}, ${this.player.y.toFixed(1)}` : 'n/a';
    } catch (e) {
      console.warn('Level stats logging failed', e);
    }
  }

  // Progress now tracked server-side by IP address for guests

  // Save level progress to server using UserJumpProgress model (or guest session)
  async saveJavaScriptLevelProgress(levelReached) {

    try {
      const response = await fetch('/api/jump_game/save_progress', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
        },
        credentials: 'include',
        body: JSON.stringify({
          jump_level_reached: levelReached,
          total_gems: this.totalGemsCollected || 0
        })
      });

      if (response.ok) {
        const data = await response.json();
        // Safety: ensure any lingering UI is removed
        if (this.levelCompleteUI && Array.isArray(this.levelCompleteUI)) {
          try {
            this.levelCompleteUI.forEach(el => el && el.destroy && el.destroy());
          } catch (e) {
            console.warn('⚠️ Error cleaning lingering completion UI:', e);
          }
          this.levelCompleteUI = null;
        }
        await this.loadServerLevel(levelReached);
        // Reset flags after new level has been generated
        this.levelCompleting = false;
        this.levelTransitioning = false;
      } else {
        console.error(`❌ Failed to save progress: ${response.statusText}`);
      }
    } catch (error) {
      console.error(`❌ Error saving progress: ${error.message}`);
    }
  }

  // Save gem progress to server
  async saveGemProgress(gemsCollected) {
    try {
      const response = await fetch('/api/jump_game/save_gems', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
        },
        credentials: 'include',
        body: JSON.stringify({
          gems_collected: gemsCollected
        })
      });

      if (response.ok) {
        const data = await response.json();
      } else {
        console.error(`❌ Failed to save gem: ${response.statusText}`);
      }
    } catch (error) {
      console.error(`❌ Error saving gem: ${error.message}`);
    }
  }

  // Award coin(s) to the current user via API
  async awardCoinsToUser(amount = 1) {
    try {
      const response = await fetch('/api/v1/user_rewards/add_coin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
        },
        credentials: 'include'
      });

      if (!response.ok) {
        console.error(`❌ Failed to award coin(s): ${response.statusText}`);
      }
    } catch (error) {
      console.error(`❌ Error awarding coin(s): ${error.message}`);
    }
  }

  createJavaScriptPlatform(x, y, width, height, color, type = 'regular') {
    // Responsive block size for mobile usability
    const blockSize = this.isMobile ? 40 : 32;
    const numBlocks = Math.max(1, Math.ceil(width / blockSize));
    const effectiveWidth = numBlocks * blockSize;

    // Create a group to hold all the block sprites for this platform
    const platformGroup = this.add.group();

    // Create individual block sprites
    for (let i = 0; i < numBlocks; i++) {
      const blockX = x - (numBlocks * blockSize) / 2 + (i * blockSize) + blockSize / 2;
      const blockY = y;

      let blockSprite;
      if (this.textures.exists('block')) {
        blockSprite = this.add.image(blockX, blockY, 'block');
        blockSprite.setDisplaySize(blockSize, blockSize);
      } else {
        // Fallback to colored rectangle if sprite doesn't load
        blockSprite = this.add.rectangle(blockX, blockY, blockSize, blockSize, color);
        blockSprite.setStrokeStyle(2, 0x654321);
      }

      blockSprite.setDepth(1);
      platformGroup.add(blockSprite);
    }

    // Create invisible physics body for the entire platform
    const platform = this.add.rectangle(x, y, effectiveWidth, blockSize, 0x000000, 0);
    platform.setDepth(0); // Behind the visual blocks

    // Mark platform type for identification
    platform.platformType = type;
    platform.isGroundPlatform = (type === 'ground');
    platform.visualBlocks = platformGroup; // Keep reference to visual blocks
    // For cleanup logic and easier respawn anchoring
    platform.platformY = y;

    // Add debugging info for goal platforms
    if (type === 'goal') {
      // Make goal platform blocks golden/yellow tinted
      platformGroup.children.entries.forEach(block => {
        if (block.setTint) {
          block.setTint(0xFFD700); // Gold tint for goal platform
        }
      });
    }

    // Ensure platform has a static physics body
    if (!platform.body) {
      this.physics.add.existing(platform, true);
    }

    // Add to physics group
    this.platforms.add(platform);

    // Slightly enlarge physics body for forgiving landings
    if (platform.body && platform.body.setSize) {
      const extraPad = Math.max(16, Math.round(effectiveWidth * 0.12));
      const collisionWidth = effectiveWidth + extraPad;
      const collisionHeight = blockSize;
      platform.body.setSize(collisionWidth, collisionHeight);
      if (platform.body.updateFromGameObject) {
        platform.body.updateFromGameObject();
      }
    }

    return platform;
  }

  // Create a floating coin at x,y
  createCoin(x, y, options = {}) {
    const {
      move = false,
      moveRange = 140,
      moveDuration = 2600,
      startDelay = Math.random() * 800
    } = options;

    let coin;
    if (this.textures.exists('coin')) {
      coin = this.add.sprite(x, y, 'coin');
      coin.setScale(this.isMobile ? 0.9 : 0.8);
    } else {
      coin = this.add.circle(x, y, 12, 0xF1C40F);
    }

    this.physics.add.existing(coin);
    // Larger pickup radius for accesibility
    coin.body.setSize(50, 50);
    coin.setDepth(8);
    this.coins.add(coin);
    coin.coinY = y;

    // Gentle bounce animation
    this.tweens.add({
      targets: coin,
      y: coin.y - 12,
      duration: 1200,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });

    // Optional horizontal movement (left-right patrol)
    if (move) {
      const gameWidth = 1200; // Use full game width for coin movement
      const minX = 24;
      const maxX = gameWidth - 24;
      const targetLeft = Math.max(minX, x - moveRange);
      const targetRight = Math.min(maxX, x + moveRange);
      this.tweens.add({
        targets: coin,
        x: targetRight,
        duration: moveDuration,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
        delay: startDelay
      });
    }

    // Slow spin shimmer if sprite
    if (this.textures.exists('coin')) {
      this.tweens.add({
        targets: coin,
        angle: 360,
        duration: 2400,
        repeat: -1,
        ease: 'Linear'
      });
    }

    return coin;
  }

  // Generate ambient coins across the board, not tied to platforms
  generateAmbientCoins(levelNumber, groundY, goalY) {
    const gameWidth = 1200; // Use full game width for coin placement
    // Keep coins scarce: 4-5 per level
    const minCoins = 4;
    const maxCoins = 5;
    const coinCount = minCoins + Math.floor(Math.random() * (maxCoins - minCoins + 1));

    for (let i = 0; i < coinCount; i++) {
      // Spread coins vertically from just above ground up towards the goal area
      const y = groundY - 80 - Math.random() * Math.max(200, (groundY - goalY) - 200);
      // Anywhere across the board width (not just play lane)
      const x = 24 + Math.random() * (gameWidth - 48);
      const move = Math.random() < 0.6; // many coins patrol left-right
      const moveRange = 100 + Math.random() * 260; // 100-360px range
      const moveDuration = 2000 + Math.random() * 2500; // 2.0-4.5s
      const startDelay = Math.random() * 1200;
      this.createCoin(x, y, { move, moveRange, moveDuration, startDelay });
    }
  }

  generatePlatformChunk(fromY, toY) {
    const gameWidth = 1200; // Use full game width for platform chunk placement
    const chunkHeight = Math.abs(fromY - toY);
    const heightFromGround = 600 - toY; // How high we are from ground
    const level = Math.floor(heightFromGround / 1000); // Level every 100m (1000 units = 100m)


    // Different patterns based on level
    const patterns = this.getLevelPatterns(level);
    let selectedPattern;

    if (!patterns || patterns.length === 0) {
      console.error(`❌ No patterns found for level ${level}! Using fallback.`);
      // Fallback to level 1 patterns
      const fallbackPatterns = this.getLevelPatterns(1);
      selectedPattern = fallbackPatterns[0];
    } else {
      selectedPattern = patterns[Math.floor(Math.random() * patterns.length)];
    }

        let currentY = fromY;
    // MUCH MORE CHALLENGING SPACING - platforms can be far apart!
    const minStepSize = level <= 2 ? 60 : 80;  // Start with bigger jumps after level 2
    const maxStepSize = level <= 2 ? 80 : 110; // Some platforms will be barely reachable
    const stepSize = minStepSize + Math.random() * (maxStepSize - minStepSize);

    // NO MORE SAFETY PLATFORMS - let players earn their progress!

    selectedPattern.platforms.forEach((platformDef, index) => {
      const y = currentY - (stepSize * (index + 1)); // Skip y=0 to space out more
      const x = this.calculatePlatformX(platformDef.x, gameWidth);

      this.createPlatform(x, y, platformDef.size);

      // Much fewer energy gems - make them precious!
      if (platformDef.hasOrb || (Math.random() < 0.3 && index % selectedPattern.orbFrequency === 0)) {
        this.createEnergyOrb(x, y - 40);
      }
    });

    // Update highest generated point
    this.highestGeneratedY = Math.min(this.highestGeneratedY, toY);
  }

      getLevelPatterns(level) {
    const patterns = [];

    // HARDCORE CHALLENGE MODE - Way fewer platforms, some barely reachable!


    if (level <= 1) {
      // Beginner level - Still reasonable but fewer platforms
      patterns.push(
        {
          platforms: [
            { x: 0.3, size: 'large' },
            { x: 0.7, size: 'medium' },
            { x: 0.2, size: 'medium', hasOrb: true }
          ],
          orbFrequency: 2
        },
        {
          platforms: [
            { x: 0.6, size: 'large' },
            { x: 0.1, size: 'medium' },
            { x: 0.8, size: 'medium' }
          ],
          orbFrequency: 2
        }
      );
    } else if (level <= 4) {
      // Early challenge - Mix of reachable and edge platforms
      patterns.push(
        {
          platforms: [
            { x: 0.15, size: 'medium' },  // Near edge - barely reachable
            { x: 0.6, size: 'small' },
            { x: 0.9, size: 'small', hasOrb: true },   // Far edge
            { x: 0.25, size: 'medium' }
          ],
          orbFrequency: 4
        },
        {
          platforms: [
            { x: 0.8, size: 'medium' },
            { x: 0.1, size: 'small' },    // Edge platform
            { x: 0.7, size: 'small' }
          ],
          orbFrequency: 4
        }
      );
    } else if (level <= 8) {
      // Medium challenge - Fewer platforms, more extreme positions
      patterns.push(
        {
          platforms: [
            { x: 0.05, size: 'small' },   // Very edge - expert jump needed!
            { x: 0.6, size: 'medium' },
            { x: 0.95, size: 'small', hasOrb: true },  // Other extreme edge
            { x: 0.3, size: 'small' }
          ],
          orbFrequency: 5
        },
        {
          platforms: [
            { x: 0.85, size: 'small' },
            { x: 0.2, size: 'medium' },
            { x: 0.9, size: 'tiny' }      // Tiny platform at edge!
          ],
          orbFrequency: 5
        }
      );
    } else if (level <= 15) {
      // Hard challenge - Mix of tiny and edge platforms
      patterns.push(
        {
          platforms: [
            { x: 0.1, size: 'tiny' },     // Tiny platform at edge
            { x: 0.7, size: 'small' },
            { x: 0.95, size: 'tiny', hasOrb: true }   // Precious gem on tiny edge platform
          ],
          orbFrequency: 6
        },
        {
          platforms: [
            { x: 0.05, size: 'small' },   // Extreme edge
            { x: 0.5, size: 'medium' },   // One reasonable platform
            { x: 0.85, size: 'tiny' }     // Tiny challenge
          ],
          orbFrequency: 6
        }
      );
    } else {
      // EXPERT HELL MODE - Mostly tiny platforms at extreme positions
      patterns.push(
        {
          platforms: [
            { x: 0.02, size: 'tiny' },    // EXTREME edge - pixel perfect needed!
            { x: 0.6, size: 'small' },
            { x: 0.98, size: 'tiny', hasOrb: true }   // Other extreme with precious gem
          ],
          orbFrequency: 8
        },
        {
          platforms: [
            { x: 0.8, size: 'tiny' },
            { x: 0.05, size: 'tiny' },    // Tiny platform at edge
            { x: 0.45, size: 'small' },   // Slightly more reasonable
            { x: 0.95, size: 'tiny' }     // Back to extreme
          ],
          orbFrequency: 8
        }
      );
    }

    return patterns;
  }

  calculatePlatformX(relativeX, gameWidth = 1200) {
    // Convert relative position (0-1) to actual X coordinate
    // Use a centered play lane on desktop so platforms remain reachable
    const playLaneWidth = this.isMobile ? gameWidth : Math.min(gameWidth, 900);
    const laneStartX = (gameWidth - playLaneWidth) / 2;

    // Responsive margin based on lane width
    const basePercent = this.isMobile ? 0.18 : 0.10; // wider margin on mobile to keep pads reachable
    const minMargin = this.isMobile ? 64 : 50;
    const maxMargin = this.isMobile ? 140 : 120;
    const margin = Math.max(minMargin, Math.min(maxMargin, playLaneWidth * basePercent));

    const xWithinLane = margin + (relativeX * (playLaneWidth - 2 * margin));
    const clampedWithinLane = Math.max(margin, Math.min(playLaneWidth - margin, xWithinLane));
    return laneStartX + clampedWithinLane;
  }

        createPlatform(x, y, size) {
    const sizes = {
      tiny: 45,
      small: 60,
      medium: 79,
      large: 98
    };

    const mobileScale = this.isMobile ? 1.35 : 1.0;
    const width = Math.round((sizes[size] || sizes.medium) * mobileScale);
    let platform;

    // Try to use lilypad sprite, fallback to rectangle if not available
    if (false) { // Disabled lilypad sprite usage
      platform = this.add.sprite(x, y, 'lilypad');

            // Scale the lilypad to match the desired platform width
      // Assuming lilypad image is much larger - scale it down significantly
      const lilypadScale = width / 700; // Adjusted for 25% smaller lilypads
      platform.setScale(lilypadScale);

      // Set depth so lilypad appears behind the frog
      platform.setDepth(5);

    } else {
      // Use rectangle collider (invisible) and block tiles for visuals
      const blockSize = this.isMobile ? 40 : 32;
      platform = this.add.rectangle(x, y, width, blockSize, 0xD2691E, 0);
      platform.setDepth(5);
      // if (this.textures.exists('block')) {
      //   const cols = Math.ceil(width / 32);
      //   for (let i = 0; i < cols; i++) {
      //     const tileX = x - width / 2 + 16 + i * 32;
      //     const tileY = y - 16; // align tile to collider top
      //     const tile = this.add.sprite(tileX, tileY, 'block', 0);
      //     tile.setDepth(4);
      //   }
      // }
    }

    this.physics.add.existing(platform, true);

        // Set physics body slightly larger than visual for forgiving landings
    const extraPad = Math.max(16, Math.round(width * 0.12));
    const collisionWidth = width + extraPad;
    const collisionHeight = this.isMobile ? 40 : 32;
    platform.body.setSize(collisionWidth, collisionHeight);

    // Static bodies (created with 'true' parameter) are already immovable by default

    this.platforms.add(platform);

    // Add gentle side-to-side movement to make lilypads challenging!
    const moveDistance = 30 + (Math.random() * 40); // Random movement distance 30-70px
    const moveDuration = 2000 + (Math.random() * 2000); // Random speed 2-4 seconds
    const startDelay = Math.random() * 1000; // Random start delay for variety

    // Initialize platform's previous position for moving platform physics
    platform.previousX = platform.x;

    this.tweens.add({
      targets: platform,
      x: platform.x + moveDistance,
      duration: moveDuration,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
      delay: startDelay
    });

    // Store platform info for cleanup
    platform.platformY = y;
  }

      createEnergyOrb(x, y) {
    let orb;

    // Try to use gem sprite, fallback to circle if not available
    if (this.textures.exists('gem')) {
      orb = this.add.sprite(x, y, 'gem');
      orb.setScale(0.9); // Even bigger! Double the previous size
      orb.setTint(0xFFFFFF); // Pure white tint for sparkle effect
    } else {
      orb = this.add.circle(x, y, 12, 0xF1C40F);
    }

    this.physics.add.existing(orb);
    // Make collision area much larger than visual sprite for easier collection
    orb.body.setSize(60, 60); // Increased from 20x20 - now you just need to get close!
    // Set depth so gems appear above platforms but below or same as frog
    orb.setDepth(8);
    this.energyOrbs.add(orb);
    // Store orb info for cleanup
    orb.orbY = y;
    // Add floating animation
    this.tweens.add({
      targets: orb,
      y: orb.y - 10,
      duration: 1500,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });

    // Add sparkle/rotation animation for gems
    if (this.textures.exists('gem')) {
      // Gentle rotation
      this.tweens.add({
        targets: orb,
        rotation: Math.PI * 2,
        duration: 4000,
        repeat: -1,
        ease: 'Linear'
      });

      // Shimmer effect with scale pulsing
      this.tweens.add({
        targets: orb,
        scaleX: orb.scaleX * 1.2,
        scaleY: orb.scaleY * 1.2,
        duration: 1000,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut'
      });

      // Sparkle tint effect
      this.tweens.add({
        targets: orb,
        tintTopLeft: 0xFFFFFF,
        tintTopRight: 0xFFD700,
        tintBottomLeft: 0xFFD700,
        tintBottomRight: 0xFFFFFF,
        duration: 2000,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut'
      });
    }
  }

  createFallbackPlayer() {
    // Create player as a rectangle shape (fallback)
    this.player = this.add.rectangle(200, 550, 28, 36, 0x3498DB);
    this.physics.add.existing(this.player);
    this.player.body.setBounce(0.1);
    this.player.body.setCollideWorldBounds(true);
    this.player.body.setGravityY(600);

    // Make sure fallback player also appears above platforms
    this.player.setDepth(10);

    this.playerType = 'rectangle';
  }

  createBackgroundSystem() {
    const gameWidth = this.sys.game.config.width; // Use display width for background
    const gameHeight = this.sys.game.config.height;

    // Create a soft sky gradient background
    this.createSkyBackground(gameWidth, gameHeight);

    // Spawn layered clouds with gentle parallax
    this.spawnClouds({ count: this.isMobile ? 6 : 10 });

    // Add a couple of 🦅 eagles flying across the sky
    this.spawnEagles({ count: this.isMobile ? 1 : 2 });
  }

  createSkyBackground(gameWidth, gameHeight) {
    // Create a large single-color sky texture to avoid visible tiling seams
    const texKey = 'sky_bg_flat';
    const fullHeight = gameHeight * 3; // cover tall vertical range
    if (!this.textures.exists(texKey)) {
      const graphics = this.add.graphics();
      const skyColor = 0x87CEFA; // light sky blue
      graphics.fillStyle(skyColor, 1);
      graphics.fillRect(0, 0, gameWidth, fullHeight);
      graphics.generateTexture(texKey, gameWidth, fullHeight);
      graphics.destroy();
    }

    const sky = this.add.image(0, 0, texKey);
    sky.setOrigin(0, 0);
    sky.setScrollFactor(0.15, 0.2);
    sky.setDepth(-5);
    this.skyBackground = sky;
  }

  ensureCloudTexture() {
    const cloudKey = 'cloud_tex';
    if (this.textures.exists(cloudKey)) return cloudKey;

    const g = this.add.graphics();
    g.clear();
    // Draw a fluffy cloud with overlapping circles
    g.fillStyle(0xffffff, 0.95);
    g.fillCircle(50, 40, 22);
    g.fillCircle(70, 30, 26);
    g.fillCircle(95, 45, 24);
    g.fillCircle(75, 55, 18);
    g.fillCircle(35, 55, 18);
    g.generateTexture(cloudKey, 120, 80);
    g.destroy();
    return cloudKey;
  }

  spawnClouds({ count = 8 } = {}) {
    const cloudKey = this.ensureCloudTexture();
    const gameWidth = this.sys.game.config.width;
    const gameHeight = this.sys.game.config.height;

    this.cloudsGroup = this.add.group();

    const spawnOne = (startX) => {
      const y = -gameHeight + Math.random() * (gameHeight * 2.2);
      const cloud = this.add.image(startX ?? (Math.random() * gameWidth), y, cloudKey);
      cloud.setDepth(-3);
      cloud.setAlpha(0.9);
      const s = 0.6 + Math.random() * 0.8;
      cloud.setScale(s);
      cloud.setScrollFactor(0.25, 0.25);
      this.cloudsGroup.add(cloud);

      const distance = gameWidth + 300 + Math.random() * 200;
      const duration = 18000 + Math.random() * 12000; // 18-30s cross
      const start = cloud.x;
      const end = -200;

      const fly = () => {
        cloud.x = startX ?? (gameWidth + 200);
        cloud.y = -gameHeight + Math.random() * (gameHeight * 2.2);
        this.tweens.add({
          targets: cloud,
          x: end,
          duration,
          ease: 'Sine.easeInOut',
          onComplete: fly
        });
      };

      // initial tween from current x to end
      this.tweens.add({
        targets: cloud,
        x: end,
        duration,
        ease: 'Sine.easeInOut',
        onComplete: fly
      });
    };

    for (let i = 0; i < count; i++) spawnOne();
  }

  spawnEagles({ count = 2 } = {}) {
    const gameWidth = this.sys.game.config.width;
    const gameHeight = this.sys.game.config.height;
    this.eaglesGroup = this.add.group();

    const spawnEagle = () => {
      const y = -gameHeight * 0.8 + Math.random() * (gameHeight * 1.6);
      const eagle = this.add.text(gameWidth + 120, y, '🦅', {
        fontSize: this.isMobile ? '28px' : '36px'
      });
      eagle.setDepth(-2);
      eagle.setAlpha(0.9);
      eagle.setScrollFactor(0.3, 0.3);
      this.eaglesGroup.add(eagle);

      // Glide across the sky
      const duration = 10000 + Math.random() * 8000;
      const bob = this.tweens.add({
        targets: eagle,
        y: eagle.y + (Math.random() * 30 - 15),
        duration: 1200,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut'
      });

      const flyAcross = () => {
        eagle.x = gameWidth + 120;
        eagle.y = -gameHeight * 0.8 + Math.random() * (gameHeight * 1.6);
        this.tweens.add({
          targets: eagle,
          x: -140,
          duration,
          ease: 'Linear',
          onComplete: flyAcross
        });
      };

      this.tweens.add({
        targets: eagle,
        x: -140,
        duration,
        ease: 'Linear',
        onComplete: flyAcross
      });
    };

    for (let i = 0; i < count; i++) spawnEagle();
  }

  update() {
    if (this.isGameOver) {
      return;
    }

    // Initialize mobile input if not exists
    this.mobileInput = this.mobileInput || { left: false, right: false, jump: false, tiltAxis: 0 };

    // Movement (keyboard + mobile buttons + analog tilt)
    const keyboardAxis = (this.cursors.left.isDown || this.wasd.A.isDown) ? -1 :
      (this.cursors.right.isDown || this.wasd.D.isDown) ? 1 : 0;
    const buttonAxis = this.mobileInput.left ? -1 : this.mobileInput.right ? 1 : 0;
    const tiltAxis = typeof this.mobileInput.tiltAxis === 'number' ? this.mobileInput.tiltAxis : 0;
    const horizontalAxis = keyboardAxis || buttonAxis || tiltAxis;
    const leftPressed = horizontalAxis < -0.05;
    const rightPressed = horizontalAxis > 0.05;

    if (leftPressed) {
      this.player.body.setVelocityX(220 * horizontalAxis);
      // Flip frog to face left (only for frog sprite)
      if (this.playerType === 'frog') {
        this.player.setFlipX(true);
      }
    } else if (rightPressed) {
      this.player.body.setVelocityX(220 * horizontalAxis);
      // Flip frog to face right (default direction)
      if (this.playerType === 'frog') {
        this.player.setFlipX(false);
      }
    } else {
      this.player.body.setVelocityX(0);
    }

    // Handle jumping with double jump mechanic (keyboard + mobile)
    const jumpPressed = this.cursors.up.isDown || this.wasd.W.isDown || this.wasd.SPACE.isDown || this.mobileInput.jump;

    // Check if player is currently on ground
    const onGround = this.player.body.touching.down;

        // Reset jumps when player lands (transition from not on ground to on ground)
    if (onGround && !this.wasOnGround) {
      this.jumpsRemaining = this.maxJumps;

      // Return to idle animation when landing (only for frog sprite)
      if (this.playerType === 'frog' && this.player.anims.currentAnim && this.player.anims.currentAnim.key === 'frog_jump') {
        this.player.play('frog_idle');
      }
    }

    // Update ground state for next frame
    this.wasOnGround = onGround;

    // Check if we can jump (have jumps remaining and enough energy)
    const canJump = this.jumpsRemaining > 0 && this.energy >= this.jumpEnergyCost;

    // Handle jump input (with key press detection to prevent holding)
    // For mobile, we need to detect new jump presses differently
    if (this.mobileInput && this.mobileInput.jump) {
      // Mobile jump - check if this is a new press
      if (!this.lastMobileJumpState && canJump) {
        this.jump();
      }
      this.lastMobileJumpState = true;
    } else if (jumpPressed && canJump && !this.jumpKeyPressed) {
      // Desktop keyboard jump
      this.jump();
    } else if (!this.mobileInput || !this.mobileInput.jump) {
      this.lastMobileJumpState = false;
    }

    // Track jump key state for single press detection
    this.jumpKeyPressed = jumpPressed;

    // No moving platform physics - keeping gameplay simple
    // Moving platform physics - make frog ride with the lilypad!
    if (false && this.currentPlatform && onGround) {
      // Frog is on a platform - move frog with the platform's movement
      // Store the platform's previous position
      if (!this.currentPlatform.previousX) {
        this.currentPlatform.previousX = this.currentPlatform.x;
      }

      // Calculate how much the platform moved this frame
      const platformMovement = this.currentPlatform.x - this.currentPlatform.previousX;

      // Move the frog the same amount (but only if not actively being controlled)
      if (platformMovement !== 0 && !leftPressed && !rightPressed) {
        this.player.x += platformMovement;
      }

      // Update the platform's previous position for next frame
      this.currentPlatform.previousX = this.currentPlatform.x;
    } else if (!onGround) {
      // Frog is in the air - clear platform reference
      this.currentPlatform = null;
    }

    // Update score based on height achieved (for display only, not for winning)
    const currentHeight = Math.max(0, Math.floor((600 - this.player.y) / 10));
    if (currentHeight > this.highestPlatform) {
      this.highestPlatform = currentHeight;
      this.score = this.highestPlatform;
    }

    // DISABLED - Dynamic platform generation (using discrete levels now)
    // const generationTrigger = this.highestGeneratedY + (this.generationChunkSize * 1.5);
    // if (this.player.y < generationTrigger) {
    //   this.generatePlatformChunk(newFromY, newToY);
    // }

    // Cleanup old platforms to maintain performance
    this.cleanupOldPlatforms();

    // Natural falling system - players always land back on ground
    // No need to teleport/respawn - let physics handle it naturally

    // Quiz trigger when energy hits zero
    if (this.energy <= 0 && !this.showingQuiz) {
      this.showingQuiz = true;
      this.physics.pause(); // Pause game physics during quiz

      // Trigger quiz via React state
      if (this.setShowQuiz && this.setGamePaused) {
        this.setShowQuiz(true);
        this.setGamePaused(true);
      }
    }

    // Optional: If player somehow falls very far (beyond reasonable game bounds),
    // gently reset them to ground without teleporting
    if (this.player.y > 1200) {
      const gameWidth = this.sys.game.config.width;
      this.player.setPosition(gameWidth / 2, 530); // Center of ground level
      this.player.body.setVelocity(0, 0);
      this.energy = Math.min(this.maxEnergy, this.energy + 20);
    }

    // Don't update level display based on height - use discrete level number
    // this.currentLevel stays as the discrete level (1, 2, 3, etc.) until level completion

    // Update background theme based on height
    this.updateBackgroundTheme();
    // this.updateServerMovingPlatforms();

    // Check for level completion
    this.checkLevelCompletion();

    // Safety check during level transitions - prevent player from falling through bottom
    if (this.levelTransitioning && this.player && this.player.y > 800) {
      this.ensurePlayerOnValidPlatform();
    }

    // Update multiplayer
    if (this.multiplayerManager) {
      this.multiplayerManager.update();
    }

    // Update React UI stats
    this.updateReactStats();
  }

  jump() {
    // Consume a jump
    this.jumpsRemaining--;
    this.energy -= this.jumpEnergyCost;
    this.totalJumps++; // Track total jumps for progress

    // Update stats immediately when energy changes
    this.updateReactStats();

    // Different jump power for first vs second jump
    const jumpPower = this.jumpsRemaining === (this.maxJumps - 1) ? -450 : -400; // First jump stronger
    this.player.body.setVelocityY(jumpPower);

    // Play jump animation (only for frog sprite)
    if (this.playerType === 'frog') {
      this.player.play('frog_jump');
    }

    // Send jump to other players
    if (this.multiplayerManager) {
      this.multiplayerManager.sendPlayerJump();
    }

    // Different visual effects for first vs double jump
    const isDoubleJump = this.jumpsRemaining === 0;
    const effectColor = isDoubleJump ? 0xFF6B6B : 0xFFFFFF; // Red for double jump, white for first
    const effectSize = isDoubleJump ? 15 : 12;

    // Add visual jump effect
    const jumpEffect = this.add.circle(this.player.x, this.player.y + 18, effectSize, effectColor, 0.6);
    this.tweens.add({
      targets: jumpEffect,
      scaleX: isDoubleJump ? 3 : 2.5,
      scaleY: isDoubleJump ? 3 : 2.5,
      alpha: 0,
      duration: 300,
      onComplete: () => jumpEffect.destroy()
    });

    // Add particle effect with different colors
    const particleColor = isDoubleJump ? 0xFF6B6B : 0x3498DB;
    const particleCount = isDoubleJump ? 8 : 6;

    for (let i = 0; i < particleCount; i++) {
      const particle = this.add.circle(
        this.player.x + (Math.random() - 0.5) * 20,
        this.player.y + 18,
        isDoubleJump ? 4 : 3,
        particleColor,
        0.8
      );

      this.tweens.add({
        targets: particle,
        x: particle.x + (Math.random() - 0.5) * (isDoubleJump ? 50 : 40),
        y: particle.y + Math.random() * (isDoubleJump ? 40 : 30),
        alpha: 0,
        duration: isDoubleJump ? 500 : 400,
        onComplete: () => particle.destroy()
      });
    }
  }

  playerLanded(player, platform) {
    // This gets called when player touches a platform via COLLIDER
    this.handlePlatformLanding(player, platform, 'collider');
  }

  playerLandedOverlap(player, platform) {
    // This gets called when player overlaps with a platform

    // Goal platforms should trigger regardless of velocity or position
    if (platform.platformType === 'goal') {
      this.handlePlatformLanding(player, platform, 'overlap');
      return;
    }

    // For regular platforms, only process if player is falling down and is above the platform
    if (player.body.velocity.y >= 0 && player.y < platform.y) {
      this.handlePlatformLanding(player, platform, 'overlap');
    }
  }

  handlePlatformLanding(player, platform, detectionType) {
    // Common logic for both collision and overlap detection

    // FIRST: Check if this is a goal platform - ANY contact triggers win!
    if (platform.platformType === 'goal') {
      if (this.levelCompleting || this.levelTransitioning) {
        return;
      }
      this.handleJavaScriptLevelComplete();
      return;
    }
    // Reset jumps when landing on a platform
    if (player.body.velocity.y >= 0) { // Only reset when falling down
      this.jumpsRemaining = this.maxJumps;

      // Clear previous platform reference
      if (this.currentPlatform && this.currentPlatform.isMovingPlatform) {
        this.currentPlatform.playerOnPlatform = false;
      }

      // Store reference to current platform for moving platform physics
      this.currentPlatform = platform;

      // Mark moving platform as having player on it
      // No moving platform logic - keeping it simple

      // Return to idle animation when landing (only for frog sprite)
      if (this.playerType === 'frog' && this.player.anims.currentAnim && this.player.anims.currentAnim.key === 'frog_jump') {
        this.player.play('frog_idle');
      }
    }
  }

  collectEnergyOrb(player, orb) {
    // Send orb collection to other players first
    if (this.multiplayerManager) {
      this.multiplayerManager.sendOrbCollection(orb.x, orb.y);
    }

    orb.destroy();
    this.totalGemsCollected = (this.totalGemsCollected || 0) + 1;

    // Save gem to database
    this.saveGemProgress(1);

    // Update stats immediately when gems change
    this.updateReactStats();

    // Visual feedback with gem theme
    const collectEffect = this.add.text(orb.x, orb.y, '💎 Gem!', {
      fontSize: '16px',
      fill: '#FFD700',  // Gold color for gems
      fontWeight: 'bold',
      stroke: '#8B4513',
      strokeThickness: 2
    });

    this.tweens.add({
      targets: collectEffect,
      y: collectEffect.y - 60,
      alpha: 0,
      duration: 1200,
      ease: 'Power2',
      onComplete: () => collectEffect.destroy()
    });

    // Add gem collection sparkle effect
    for (let i = 0; i < 12; i++) {
      const sparkle = this.add.circle(
        orb.x + (Math.random() - 0.5) * 40,
        orb.y + (Math.random() - 0.5) * 40,
        3,
        Math.random() > 0.5 ? 0xFFD700 : 0xFFFFFF, // Mix of gold and white sparkles
        1.0
      );

      this.tweens.add({
        targets: sparkle,
        scaleX: 0,
        scaleY: 0,
        alpha: 0,
        y: sparkle.y - 30,
        duration: 1200,
        ease: 'Power2',
        onComplete: () => sparkle.destroy()
      });
    }
  }

  collectCoin(player, coin) {
    // Destroy coin
    coin.destroy();

    // Track coins locally and send to server
    this.totalCoinsCollected = (this.totalCoinsCollected || 0) + 1;
    this.updateReactStats();
    this.awardCoinsToUser(1);

    // Visual feedback
    const collectEffect = this.add.text(coin.x, coin.y, 'Coin!', {
      fontSize: '16px',
      fill: '#FFD700',
      fontWeight: 'bold',
      stroke: '#8B4513',
      strokeThickness: 2
    });

    this.tweens.add({
      targets: collectEffect,
      y: collectEffect.y - 60,
      alpha: 0,
      duration: 1200,
      ease: 'Power2',
      onComplete: () => collectEffect.destroy()
    });
  }

  victory() {
    if (this.isGameOver) return;

    this.isGameOver = true;
    this.physics.pause();

    // Victory celebration
    this.cameras.main.flash(1000, 255, 215, 0);

    this.gameOverText.setText(`🎉 VICTORY! 🎉\nYou reached the top!\nFinal Score: ${this.score}\n\nPress R to Play Again`);
    this.gameOverText.setStyle({
      fill: '#FFD700',
      backgroundColor: 'rgba(0,0,0,0.9)',
      padding: { x: 20, y: 10 }
    });

    // Enable restart (keyboard)
    this.input.keyboard.once('keydown-R', () => {
      this.restartGame();
    });

    // Enable mobile restart - set flag for mobile restart UI
    this.showMobileRestart = true;

    // Update React state for mobile UI
    if (this.setGameOver) {
      this.setGameOver(true);
    }
  }

  updateReactStats() {
    if (this.setGameStats) {
      const height = Math.floor((600 - this.player.y) / 10);
      this.setGameStats({
        energy: Math.ceil(this.energy),
        maxEnergy: this.maxEnergy,
        height: Math.max(0, height),
        level: this.currentLevel,
        gems: this.totalGemsCollected || 0,
        coins: this.totalCoinsCollected || 0
      });
    }
  }

  // Update moving platforms based on server data
  updateServerMovingPlatforms() {
    if (!this.serverLevelData) return;

    this.platforms.children.entries.forEach(platform => {
      if (platform.isMovingPlatform) {
        // Move platform back and forth within its range
        const distanceFromOriginal = Math.abs(platform.x - platform.originalX);

        if (distanceFromOriginal >= platform.moveRange) {
          // Reverse direction
          platform.moveDirection *= -1;
          platform.body.setVelocityX(platform.moveSpeed * platform.moveDirection);
        }

        // If player is on this moving platform, move player with it
        if (platform.playerOnPlatform && this.player && this.currentPlatform === platform) {
          // Check if player is still touching the platform (not jumping)
          const playerOnGround = this.player.body.touching.down && platform.body.touching.up;

          if (playerOnGround) {
            // Move player with the platform - Mario-style physics
            this.player.x += platform.body.velocity.x * (1/60); // Approximate frame time
          } else {
            // Player jumped off, stop tracking
            platform.playerOnPlatform = false;
            this.currentPlatform = null;
          }
        }

        // Update physics body position
        platform.body.x = platform.x - (platform.width / 2);
      }
    });
  }

  // Check if player has completed the current level
  checkLevelCompletion() {
    if (!this.serverLevelData || this.loadingLevelData) return;

    // Height is calculated for display only - level completion is handled by goal platform touch only
    // (removed height-based level completion logic)
  }

  resetPlayerToGround() {
    if (!this.player) return;

    const gameWidth = this.sys.game.config.width;
    // Find ground platforms by flag
    const groundPlatforms = this.platforms.children.entries.filter(p => p.isGroundPlatform);

    if (groundPlatforms.length > 0) {
      // Choose the ground platform whose center is closest to screen center
      const centerX = gameWidth / 2;
      const spawnPlatform = groundPlatforms.reduce((best, p) => {
        const db = Math.abs((best?.x ?? centerX) - centerX);
        const dp = Math.abs(p.x - centerX);
        return dp < db ? p : best;
      }, groundPlatforms[0]);
      this.player.x = spawnPlatform.x;
      this.player.y = spawnPlatform.y - 50;
    } else {
      // Fallback to center of screen at ground level
      this.player.x = gameWidth / 2;
      this.player.y = 530;
    }

    // Reset player velocity
    this.player.body.setVelocity(0, 0);
  }
  // Natural energy restore system - no complex respawning needed
  // Players naturally fall back to ground and get energy restored there

  gameOver(reason = "Game Over!") {
    if (this.isGameOver) return;

    this.isGameOver = true;
    this.physics.pause();

    // Screen shake effect
    this.cameras.main.shake(500, 0.02);

    this.gameOverText.setText(`${reason}\nHeight Reached: ${this.score}m\n\nPress R to Try Again`);
    this.gameOverText.setStyle({
      fill: '#E74C3C',
      backgroundColor: 'rgba(0,0,0,0.9)',
      padding: { x: 20, y: 10 }
    });

    // Enable restart (keyboard)
    this.input.keyboard.once('keydown-R', () => {
      this.restartGame();
    });

    // Enable mobile restart - set flag for mobile restart UI
    this.showMobileRestart = true;

    // Update React state for mobile UI
    if (this.setGameOver) {
      this.setGameOver(true);
    }
  }

  restartGame() {
    // Reset all game state and restart from level 1
    this.energy = 100;
    this.maxEnergy = 100;
    this.jumpEnergyCost = 5;
    this.isGameOver = false;
    this.showingQuiz = false;
    this.score = 0;
    this.highestPlatform = 0;
    this.cameraFollowPlayer = true;
    this.jumpsRemaining = 2;
    this.maxJumps = 2;
    this.jumpKeyPressed = false;
    this.wasOnGround = false;

    // Reset level-specific state
    this.currentServerLevel = 1;
    this.serverLevelData = null;
    this.userStartingLevel = 1;
    this.loadingLevelData = false;
    this.levelCompleted = false;
    this.currentLevelTargetHeight = 0;

    // Reset progress tracking
    this.totalJumps = 0;
    this.totalEnergyCollected = 0;
    this.totalGemsCollected = 0;
    this.totalCoinsCollected = 0;

    // Reset infinite generation properties
    this.highestGeneratedY = 600;
    this.currentLevel = 1;
    this.playerType = 'rectangle';

    // Reset mobile-specific state
    this.mobileInput = { left: false, right: false, jump: false, tiltAxis: 0 };
    this.lastMobileJumpState = false;
    this.showMobileRestart = false;

    // Reset moving platform physics
    this.currentPlatform = null;

    // Reset background theme
    this.currentBackgroundTheme = -1; // Force theme reset on next update

    // Clear the game over text and reset styling
    this.gameOverText.setText('');
    this.gameOverText.setStyle({
      backgroundColor: 'transparent',
      padding: { x: 0, y: 0 }
    });

    // Update React stats on restart
    this.updateReactStats();

    // Reset React game over state
    if (this.setGameOver) {
      this.setGameOver(false);
    }

    // Disconnect and reconnect multiplayer
    if (this.multiplayerManager) {
      this.multiplayerManager.disconnect();
      this.multiplayerManager = null;
    }

    // Restart the scene
    this.scene.restart();
  }

  // Safety method to ensure player is on a valid platform
  ensurePlayerOnValidPlatform() {
    if (!this.player) return;

    const gameWidth = this.sys.game.config.width;

    // Find ground platforms by flag
    const groundPlatforms = this.platforms.children.entries.filter(p => p.isGroundPlatform);

    if (groundPlatforms.length > 0) {
      // Choose the ground platform whose center is closest to screen center
      const centerX = gameWidth / 2;
      const spawnPlatform = groundPlatforms.reduce((best, p) => {
        const db = Math.abs((best?.x ?? centerX) - centerX);
        const dp = Math.abs(p.x - centerX);
        return dp < db ? p : best;
      }, groundPlatforms[0]);

      // Position player directly above the ground platform
      this.player.setPosition(spawnPlatform.x, spawnPlatform.y - 50);
      this.player.body.setVelocity(0, 0);
      this.jumpsRemaining = this.maxJumps;
    } else {
      // No ground platforms found, use default position
      this.player.setPosition(gameWidth / 2, 530);
      this.player.body.setVelocity(0, 0);
      this.jumpsRemaining = this.maxJumps;
    }
  }

  clearLevelObjects() {
    try {
      // Clean up platforms and their visual tiles
      if (this.platforms && this.platforms.children && this.platforms.children.entries) {
        this.platforms.children.entries.forEach(platform => {
          // Kill any tweens targeting the platform
          this.tweens && this.tweens.killTweensOf && this.tweens.killTweensOf(platform);

          // Destroy visual blocks group if present
          if (platform.visualBlocks) {
            const blocks = platform.visualBlocks.children?.entries || [];
            blocks.forEach(block => {
              this.tweens && this.tweens.killTweensOf && this.tweens.killTweensOf(block);
              block.destroy && block.destroy();
            });
            platform.visualBlocks.clear && platform.visualBlocks.clear(true);
            platform.visualBlocks.destroy && platform.visualBlocks.destroy(true);
            platform.visualBlocks = null;
          }

          // Destroy the platform collider itself
          platform.destroy && platform.destroy();
        });

        // Finally clear the platforms group to drop references
        this.platforms.clear(true, true);
      }

      // Clean up energy orbs and any tweens on them
      if (this.energyOrbs && this.energyOrbs.children && this.energyOrbs.children.entries) {
        this.energyOrbs.children.entries.forEach(orb => {
          this.tweens && this.tweens.killTweensOf && this.tweens.killTweensOf(orb);
          orb.destroy && orb.destroy();
        });
        this.energyOrbs.clear(true, true);
      }

      // Clean up coins and any tweens
      if (this.coins && this.coins.children && this.coins.children.entries) {
        this.coins.children.entries.forEach(c => {
          this.tweens && this.tweens.killTweensOf && this.tweens.killTweensOf(c);
          c.destroy && c.destroy();
        });
        this.coins.clear(true, true);
      }
    } catch (e) {
      console.warn('⚠️ Error while clearing level objects:', e);
    }
  }

  // Save only the progress (do not load a level). Used when React reloads the game area.
  async saveProgressOnly(levelReached) {
    try {
      const response = await fetch('/api/jump_game/save_progress', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
        },
        credentials: 'include',
        body: JSON.stringify({
          jump_level_reached: levelReached,
          total_gems: this.totalGemsCollected || 0
        })
      });

      if (response.ok) {
        const data = await response.json();
        this.currentLevel = levelReached;
        this.levelCompleting = false;
        this.levelTransitioning = false;
        return true;
      } else {
        console.error(`❌ Failed to save progress: ${response.statusText}`);
        return false;
      }
    } catch (error) {
      console.error(`❌ Error saving progress: ${error.message}`);
      return false;
    }
  }

  cleanupOldPlatforms() {
    // Remove platforms/orbs/coins that are far below the highest point reached
    // so they don't accumulate when falling back down
    const highestReachedY = 600 - (this.score * 10); // convert meters back to Y
    const cleanupThreshold = highestReachedY + (this.platformCleanupDistance || 2000);

    // Platforms
    if (this.platforms && this.platforms.children && this.platforms.children.entries) {
      this.platforms.children.entries.slice().forEach(platform => {
        const py = platform.platformY ?? platform.y;
        if (typeof py === 'number' && py > cleanupThreshold) {
          if (platform.visualBlocks) {
            platform.visualBlocks.destroy(true);
            platform.visualBlocks = null;
          }
          this.platforms.remove(platform, true, true);
          platform.destroy && platform.destroy();
        }
      });
    }

    // Orbs
    if (this.energyOrbs && this.energyOrbs.children && this.energyOrbs.children.entries) {
      this.energyOrbs.children.entries.slice().forEach(orb => {
        const oy = orb.orbY ?? orb.y;
        if (typeof oy === 'number' && oy > cleanupThreshold) {
          this.energyOrbs.remove(orb, true, true);
          orb.destroy && orb.destroy();
        }
      });
    }

    // Coins
    if (this.coins && this.coins.children && this.coins.children.entries) {
      this.coins.children.entries.slice().forEach(c => {
        const cy = c.coinY ?? c.y;
        if (typeof cy === 'number' && cy > cleanupThreshold) {
          this.coins.remove(c, true, true);
          c.destroy && c.destroy();
        }
      });
    }
  }

  updateBackgroundTheme() {
    // No-op: background is static single-color; clouds/eagles animate via tweens
  }
}

const JumpingGame = ({ current_user, quizCode }) => {
  const gameRef = useRef(null);
  const phaserGameRef = useRef(null);
  const musicManager = useMusicManager('jumpingGame');
  const [isGameLoaded, setIsGameLoaded] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const [showMusicModal, setShowMusicModal] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isGameOver, setIsGameOver] = useState(false);
  const [useTiltControls, setUseTiltControls] = useState(false);
  const [tiltSupported, setTiltSupported] = useState(false);
  const [tiltPermissionState, setTiltPermissionState] = useState('unknown');
  const [quizTitle, setQuizTitle] = useState(null);
  const [isMultiplayerEnabled, setIsMultiplayerEnabled] = useState(true);
  const [connectedPlayers, setConnectedPlayers] = useState(0);
  const [showInstructions, setShowInstructions] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const [gameStats, setGameStats] = useState({
    energy: 100,
    maxEnergy: 100,
    height: 0,
    level: 1,
    gems: 0,
    coins: 0
  });

  // Constrained game dimensions (desktop width cap)
  const [gameDimensions, setGameDimensions] = useState({ width: 0, height: 0 });
  const [viewportWidth, setViewportWidth] = useState(0);
  const controlPreferenceSetRef = useRef(false);

  // Quiz state management
  const [showQuiz, setShowQuiz] = useState(false);
  const [gamesPaused, setGamePaused] = useState(false);
  const [showLevelComplete, setShowLevelComplete] = useState(false);
  const [currentLevel, setCurrentLevel] = useState(1);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Quiz game session tracking
  const [gameSession, setGameSession] = useState(null);
  const [questionsAnswered, setQuestionsAnswered] = useState(0);
  const [questionsCorrect, setQuestionsCorrect] = useState(0);

  // Quiz handlers
  const handleQuizSuccess = () => {
    // The QuizOverlay calls this after player answers 3 questions correctly
    // Restore to 100% energy
    const scene = phaserGameRef.current?.scene?.scenes[0];
    if (scene) {
      scene.energy = scene.maxEnergy; // Full energy restoration
      scene.updateReactStats();
    }

    // Track completion for session
    if (gameSession) {
      const newCorrect = questionsCorrect + 3; // Assuming 3 correct answers needed
      const newAnswered = questionsAnswered + 3;
      setQuestionsCorrect(newCorrect);
      setQuestionsAnswered(newAnswered);
      
      updateGameSession(gameSession.id, {
        questions_answered: newAnswered,
        questions_correct: newCorrect,
        levels_climbed: scene?.currentLevel || 1
      });
    }

    // End the quiz and resume the game
    endQuiz();
  };

  const handleQuizFailure = () => {
    // Track wrong answer for session
    if (gameSession) {
      const newAnswered = questionsAnswered + 1;
      setQuestionsAnswered(newAnswered);
      
      updateGameSession(gameSession.id, {
        questions_answered: newAnswered,
        questions_correct: questionsCorrect,
        levels_climbed: phaserGameRef.current?.scene?.scenes[0]?.currentLevel || 1
      });
    }
    
    // On wrong answer, continue with more questions (no penalty)
    // The QuizOverlay component handles showing the next question
  };

  const endQuiz = () => {
    setShowQuiz(false);
    setGamePaused(false);

    // Resume game physics and reset quiz flag
    const scene = phaserGameRef.current?.scene?.scenes[0];
    if (scene) {
      scene.physics.resume();
      scene.showingQuiz = false;
    }
  };

  // Game session management
  const startGameSession = async (quizCode) => {
    if (!current_user || !quizCode) return null;

    try {
      const response = await fetch('/api/quiz_game_sessions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
        },
        credentials: 'include',
        body: JSON.stringify({ quiz_code: quizCode })
      });

      const data = await response.json();
      if (data.success) {
        setGameSession(data.session);
        return data.session;
      }
    } catch (error) {
      console.error('Failed to start game session:', error);
    }
    return null;
  };

  const updateGameSession = async (sessionId, updates) => {
    if (!current_user || !sessionId) return;

    try {
      const response = await fetch(`/api/quiz_game_sessions/${sessionId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
        },
        credentials: 'include',
        body: JSON.stringify(updates)
      });

      const data = await response.json();
      if (data.success) {
        setGameSession(prev => ({...prev, ...data.session}));
      }
    } catch (error) {
      console.error('Failed to update game session:', error);
    }
  };

  const completeGameSession = async (sessionId) => {
    if (!current_user || !sessionId) return;

    try {
      const response = await fetch(`/api/quiz_game_sessions/${sessionId}/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
        },
        credentials: 'include'
      });

      const data = await response.json();
      if (data.success) {
        setGameSession(prev => ({...prev, ...data.session}));
        return data.session;
      }
    } catch (error) {
      console.error('Failed to complete game session:', error);
    }
    return null;
  };

  // Level completion handlers
  const handleLevelComplete = (level) => {
    setCurrentLevel(level);
    setShowLevelComplete(true);
    setGamePaused(true);
  };

  const handleNextLevel = async () => {
    if (isTransitioning) {
      return;
    }
    setIsTransitioning(true);

    if (phaserGameRef.current && phaserGameRef.current.scene.scenes[0]) {
      const scene = phaserGameRef.current.scene.scenes[0];
      try {
        // Save progress to next level only
        const ok = await scene.proceedToNextLevel();
                if (ok) {
          // Preserve player's current energy before destroying game
          const preservedEnergy = scene.energy;

          // Destroy Phaser instance and recreate cleanly to fully reload game area
          phaserGameRef.current?.destroy(true);
          phaserGameRef.current = null;

          // Brief delay to allow DOM to settle
          await new Promise(r => setTimeout(r, 30));

          // Initialize new game with preserved energy
          initPhaser(preservedEnergy);
        }
        setShowLevelComplete(false);
        setGamePaused(false);
      } catch (error) {
        console.error('Error transitioning to next level:', error);
        setShowLevelComplete(false);
        setGamePaused(false);
      } finally {
        setIsTransitioning(false);
      }
    } else {
      // No scene available; initialize fresh
      phaserGameRef.current?.destroy?.(true);
      phaserGameRef.current = null;
      initPhaser(); // No preserved health for fresh start
      setIsTransitioning(false);
    }
  };


  // Start game session for quiz mode
  useEffect(() => {
    if (quizCode && current_user && !gameSession) {
      startGameSession(quizCode);
    }
  }, [quizCode, current_user]);

  // Fetch quiz title immediately when quizCode is provided
  useEffect(() => {
    const fetchQuizTitle = async () => {
      if (quizCode && !quizTitle) {
        try {
          const url = `/api/quiz/question?quiz_code=${quizCode}`;
          const response = await fetch(url);
          const data = await response.json();
          
          if (data.success && data.question && data.question.quiz_title) {
            setQuizTitle(data.question.quiz_title);
          }
        } catch (error) {
          console.error('Error fetching quiz title:', error);
        }
      }
    };
    
    fetchQuizTitle();
  }, [quizCode, quizTitle]);

  // Detect mobile device and tilt support
  useEffect(() => {
    const checkMobile = () => {
      const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
                           window.innerWidth <= 1200;
      setIsMobile(isMobileDevice);

      // Check if device orientation is supported (safely)
      let hasTiltSupport = false;
      if (isMobileDevice) {
        try {
          // Check if DeviceOrientationEvent exists and is supported
          hasTiltSupport = 'DeviceOrientationEvent' in window &&
            (typeof DeviceOrientationEvent.requestPermission === 'function' ||
             typeof window.ondeviceorientation !== 'undefined');
        } catch (error) {
          // If any error occurs in detection, assume no support
          hasTiltSupport = false;
        }
      }
      setTiltSupported(hasTiltSupport);
      if (isMobileDevice && hasTiltSupport && !controlPreferenceSetRef.current) {
        setUseTiltControls(true);
      }
      if (!hasTiltSupport) {
        setTiltPermissionState('unsupported');
      }

      // Update game dimensions (constrained max width for better mobile-friendly experience)
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const maxGameWidth = 1200; // Limit game width for better mobile-friendly experience
      const computedWidth = Math.min(vw, maxGameWidth);
      setGameDimensions({ width: computedWidth, height: vh });
      setViewportWidth(vw);

      // Propagate mobile flag to scene if running
      const scene = phaserGameRef.current?.scene?.scenes?.[0];
      if (scene) {
        scene.isMobile = isMobileDevice;
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Set up multiplayer player count tracking
  useEffect(() => {
    const updatePlayerCount = () => {
      const manager = phaserGameRef.current?.scene?.scenes?.[0]?.multiplayerManager;
      setConnectedPlayers(manager?.otherPlayers?.size || 0);
    };

    updatePlayerCount();
    const interval = setInterval(updatePlayerCount, 1000);

    return () => clearInterval(interval);
  }, [isGameLoaded, isMultiplayerEnabled]);

  // Device orientation/tilt controls
  useEffect(() => {
    if (!isMobile || !tiltSupported || !useTiltControls) return;

    if (typeof DeviceOrientationEvent !== 'undefined' &&
        typeof DeviceOrientationEvent.requestPermission === 'function' &&
        tiltPermissionState !== 'granted') {
      setTiltPermissionState('needs_permission');
      return;
    }

    const deadZone = 5;
    const maxTilt = 28;

    const handleOrientation = (event) => {
      if (!phaserGameRef.current || !phaserGameRef.current.scene.scenes[0]) return;

      const scene = phaserGameRef.current.scene.scenes[0];
      scene.mobileInput = scene.mobileInput || {};

      // Get gamma (left-right tilt): negative = left, positive = right
      const gamma = typeof event.gamma === 'number' ? event.gamma : 0;
      const magnitude = Math.abs(gamma);

      if (magnitude <= deadZone) {
        scene.mobileInput.tiltAxis = 0;
      } else {
        const scaled = Math.min((magnitude - deadZone) / (maxTilt - deadZone), 1);
        scene.mobileInput.tiltAxis = Math.sign(gamma) * scaled;
      }

      scene.mobileInput.left = false;
      scene.mobileInput.right = false;
    };

    setTiltPermissionState('active');
    window.addEventListener('deviceorientation', handleOrientation);

    return () => {
      window.removeEventListener('deviceorientation', handleOrientation);
      const scene = phaserGameRef.current?.scene?.scenes?.[0];
      if (scene?.mobileInput) {
        scene.mobileInput.tiltAxis = 0;
      }
    };
  }, [isMobile, tiltSupported, useTiltControls, tiltPermissionState]);

  // Reset mobile input when switching control modes
  useEffect(() => {
    if (phaserGameRef.current && phaserGameRef.current.scene.scenes[0]) {
      const scene = phaserGameRef.current.scene.scenes[0];
      scene.mobileInput = { left: false, right: false, jump: false, tiltAxis: 0 };
    }
  }, [useTiltControls]);

  const currentScene = () => phaserGameRef.current?.scene?.scenes?.[0];

  const resetMobileInput = () => {
    const scene = currentScene();
    if (scene) {
      scene.mobileInput = { left: false, right: false, jump: false, tiltAxis: 0 };
    }
  };

  const handleEnableTiltControls = async () => {
    controlPreferenceSetRef.current = true;

    if (!tiltSupported) {
      setTiltPermissionState('unsupported');
      setUseTiltControls(false);
      return;
    }

    try {
      if (typeof DeviceOrientationEvent !== 'undefined' &&
          typeof DeviceOrientationEvent.requestPermission === 'function') {
        const permission = await DeviceOrientationEvent.requestPermission();
        if (permission !== 'granted') {
          setTiltPermissionState('denied');
          setUseTiltControls(false);
          resetMobileInput();
          return;
        }
      }

      setTiltPermissionState('granted');
      setUseTiltControls(true);
    } catch (error) {
      console.error('❌ Error requesting tilt permission:', error);
      setTiltPermissionState('denied');
      setUseTiltControls(false);
      resetMobileInput();
    }
  };

  const handleUseTouchControls = () => {
    controlPreferenceSetRef.current = true;
    setUseTiltControls(false);
    resetMobileInput();
  };

  const setMobileDirection = (direction, isPressed) => {
    if (useTiltControls) return;

    const scene = currentScene();
    if (!scene) return;

    scene.mobileInput = scene.mobileInput || { left: false, right: false, jump: false, tiltAxis: 0 };
    scene.mobileInput[direction] = isPressed;
    scene.mobileInput.tiltAxis = 0;

    if (isPressed) {
      const oppositeDirection = direction === 'left' ? 'right' : 'left';
      scene.mobileInput[oppositeDirection] = false;
    }
  };

  const pulseMobileJump = () => {
    const scene = currentScene();
    if (!scene) return;

    scene.mobileInput = scene.mobileInput || { left: false, right: false, jump: false, tiltAxis: 0 };
    scene.mobileInput.jump = true;

    setTimeout(() => {
      if (scene.mobileInput) {
        scene.mobileInput.jump = false;
      }
    }, 140);
  };

  const handleTouchJump = pulseMobileJump;

  const handleMobileRestart = () => {
    if (phaserGameRef.current && phaserGameRef.current.scene.scenes[0]) {
      const scene = phaserGameRef.current.scene.scenes[0];
      scene.restartGame();
    }
  };

  useEffect(() => {
    // Handle window resize
    const handleResize = () => {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const isMobileNow = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || vw <= 1200;
      const maxGameWidth = 1200; // Limit game width for better mobile-friendly experience
      const newWidth = Math.min(vw, maxGameWidth);
      setGameDimensions({ width: newWidth, height: vh });
      setViewportWidth(vw);

      if (phaserGameRef.current) {
        phaserGameRef.current.scale.resize(newWidth, vh);
        const scene = phaserGameRef.current.scene?.scenes?.[0];
        if (scene) {
          // Update world bounds and camera deadzone to new width
          // Always use maxGameWidth for physics world so mobile can access full gameplay area
          scene.physics.world.setBounds(0, -10000, maxGameWidth, 12000);
          scene.cameras?.main?.setDeadzone(newWidth * 0.15, 80);
          scene.isMobile = isMobileNow;
        }
      }
    };

    if (gameRef.current && !phaserGameRef.current && gameStarted) {
      // For initial game start, no preserved health
      initPhaser();
      window.addEventListener('resize', handleResize);
    }

    return () => {
      if (phaserGameRef.current) {
        phaserGameRef.current.destroy(true);
        phaserGameRef.current = null;
      }
      window.removeEventListener('resize', handleResize);
    };
  }, [gameStarted]);

  // Auto-start the game after component mounts (bypassing instruction screen)
  useEffect(() => {
    // Wait for DOM to be ready and add a small delay to ensure WebGL context is available
    const timer = setTimeout(() => {
      if (gameRef.current && !gameStarted) {
        setGameStarted(true);
      }
    }, 100); // Small delay to ensure DOM and WebGL are ready

    return () => clearTimeout(timer);
  }, []); // Run once on mount

  // Start the game
  const startGame = () => {
    setShowInstructions(false);
    setGameStarted(true);
  };

  // Helper to (re)initialize the Phaser game instance
  const initPhaser = (preservedEnergy = null) => {
    const config = {
      type: Phaser.AUTO,
      width: gameDimensions.width || window.innerWidth,
      height: gameDimensions.height || window.innerHeight,
      parent: gameRef.current,
      backgroundColor: '#87CEEB',
      scale: {
        mode: Phaser.Scale.RESIZE,
        autoCenter: Phaser.Scale.CENTER_BOTH
      },
      physics: {
        default: 'arcade',
        arcade: {
          gravity: { y: 0 },
          debug: false
        }
      },
      scene: GameScene
    };

    phaserGameRef.current = new Phaser.Game(config);
    setIsGameLoaded(true);

    setTimeout(() => {
      if (phaserGameRef.current && phaserGameRef.current.scene.scenes[0]) {
        const scene = phaserGameRef.current.scene.scenes[0];
        scene.setGameStats = setGameStats;
        scene.setGameOver = setIsGameOver;
        scene.setShowQuiz = setShowQuiz;
        scene.setGamePaused = setGamePaused;
        scene.setShowLevelComplete = handleLevelComplete;
        scene.isMobile = isMobile;
        scene.quizCode = quizCode;

        // Restore preserved energy if available
        if (preservedEnergy !== null) {
          scene.energy = preservedEnergy;
        }
      }
    }, 100);
  };

  return (
    <div className="relative w-screen h-screen overflow-hidden">
      {/* Loading/Instructions Screen */}
      {showInstructions && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-blue-900 via-purple-900 to-green-900">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 mx-4 max-w-lg text-center border border-white/20 shadow-2xl">
            <h1 className="text-4xl font-black text-white mb-6 tracking-wider">
              🐸 BRAIN CLIMB
            </h1>

            <div className="text-left space-y-4 text-white/90">
              <div className="bg-black/30 rounded-lg p-4">
                <h3 className="text-yellow-300 font-bold text-lg mb-2">🎯 How to Play</h3>
                <p>Help your frog jump across challenging blocks! When your energy runs out, answer quiz questions to keep climbing and collect gems and coins along the way!</p>
              </div>

              <div className="bg-black/30 rounded-lg p-4">
                <h3 className="text-green-300 font-bold text-lg mb-2">🎮 Controls</h3>
                <div className="space-y-2 text-sm">
                  {isMobile ? (
                    <>
                      <p>• <strong>Tap HOP</strong> to jump (tap again for another hop!)</p>
                      <p>• <strong>Tilt your phone</strong> to move, or switch to touch arrows</p>
                    </>
                  ) : (
                    <>
                      <p>• <strong>SPACE or UP arrow</strong> to jump (press twice for double jump!)</p>
                      <p>• <strong>LEFT/RIGHT arrows</strong> or WASD to move</p>
                    </>
                  )}
                </div>
              </div>

              <div className="bg-black/30 rounded-lg p-4">
                <h3 className="text-purple-300 font-bold text-lg mb-2">💎 Tips</h3>
                <div className="space-y-1 text-sm">
                  <p>• Blocks move - time your jumps carefully!</p>
                  <p>• Answer quiz questions when energy runs out to continue</p>
                  <p>• Collect gems for points, not energy</p>
                  <p>• Collect coins along the way</p>
                  <p>• {current_user ? 'Your progress is saved!' : 'Sign up to save your progress!'}</p>
                </div>
              </div>
            </div>

            <button
              onClick={startGame}
              className="mt-6 px-12 py-4 bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-400 hover:to-blue-400 text-white rounded-2xl font-black text-xl transition-all duration-200 shadow-xl hover:shadow-2xl hover:scale-105"
            >
              🚀 START HOPPING!
            </button>
          </div>
        </div>
      )}

      {/* Music Control Modal */}
      <MusicControlModal
        isOpen={showMusicModal}
        onClose={() => setShowMusicModal(false)}
        musicManager={musicManager}
        isMobile={isMobile}
      />

      {isMobile && (
      <div className="absolute top-0 left-0 right-0 z-20 bg-gradient-to-r from-blue-900/80 to-purple-900/80 backdrop-blur-sm border-b border-blue-400/30">
        <div className="flex items-center justify-between px-3 py-1 flex-col gap-1">
          <h1 className="text-lg font-black text-white drop-shadow-lg tracking-wider smooth-text"
              style={{
                fontFamily: 'system-ui, -apple-system, sans-serif',
                textShadow: '2px 2px 4px rgba(0,0,0,0.8), 0 0 20px rgba(59, 130, 246, 0.5)',
                background: 'linear-gradient(45deg, #60a5fa, #a78bfa)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
            BRAIN CLIMB
          </h1>

          {/* Quiz Title - Mobile */}
          {quizTitle && (
            <p className="text-sm text-blue-200 font-medium opacity-90 tracking-wide">
              📚 {quizTitle}
            </p>
          )}

          {/* Game Stats */}
          {isMobile && (
          <div className="flex items-center text-white gap-2 text-xs">
            {/* Energy Bar */}
            <div className="flex items-center gap-1">
              <span className="text-xs font-semibold">⚡</span>
              <div className="relative w-16 h-2 bg-black/40 rounded-full border border-blue-400/30">
                <div
                  className={`absolute left-0 top-0 h-full rounded-full transition-all duration-300 ${
                    gameStats.energy > 50 ? 'bg-green-500' :
                    gameStats.energy > 25 ? 'bg-yellow-500' : 'bg-red-500'
                  }`}
                  style={{ width: `${(gameStats.energy / gameStats.maxEnergy) * 100}%` }}
                />
              </div>
              <span className="text-xs font-mono">{gameStats.energy}/{gameStats.maxEnergy}</span>
            </div>

            {/* Level */}
            <div className="flex items-center gap-1">
              <span className="text-blue-300 text-xs font-semibold">Lv</span>
              <span className="text-yellow-300 text-sm font-bold font-mono">{gameStats.level}</span>
            </div>

            {/* Height */}
            <div className="flex items-center gap-1">
              <span className="text-purple-300 text-xs font-semibold">Height</span>
              <span className="text-white text-sm font-bold font-mono">{gameStats.height}m</span>
            </div>

            {/* Gems */}
            <div className="flex items-center gap-1">
              <span className="text-xs font-semibold">💎</span>
              <span className="text-sm font-bold font-mono">{gameStats.gems || 0}</span>
            </div>

            {/* Coins */}
            <div className="flex items-center gap-1">
              <span className="text-xs font-semibold">💰</span>
              <span className="text-sm font-bold font-mono">{gameStats.coins || 0}</span>
            </div>
          </div>
          )}

          {/* Controls Section */}
          <div className="flex items-center gap-2">

            {/* Tilt Toggle Button - Only show on mobile if supported */}
            {isMobile && tiltSupported && (
              <button
                onClick={useTiltControls ? handleUseTouchControls : handleEnableTiltControls}
                className={`${isMobile ? 'p-1' : 'p-2'} rounded-lg font-semibold transition-all duration-200 backdrop-blur-sm border ${
                  useTiltControls
                    ? 'bg-purple-600/80 hover:bg-purple-500/80 border-purple-400/30 text-white'
                    : 'bg-gray-600/80 hover:bg-gray-500/80 border-gray-400/30 text-gray-300'
                }`}
                title={useTiltControls ? 'Switch to touch controls' : 'Switch to tilt controls'}
              >
                {useTiltControls ? '📱 Tilt' : '👆 Touch'}
              </button>
            )}


            {/* Multiplayer Toggle */}
            <button
              onClick={() => {
                const scene = phaserGameRef.current?.scene?.scenes[0];
                if (scene) {
                  if (isMultiplayerEnabled) {
                    scene.multiplayerManager?.disconnect();
                    setConnectedPlayers(0);
                  } else {
                    if (!scene.multiplayerManager) {
                      scene.multiplayerManager = new MultiplayerManager(scene);
                    }
                    scene.multiplayerManager.connect();
                  }
                }
                setIsMultiplayerEnabled(!isMultiplayerEnabled);
              }}
              className={`${isMobile ? 'p-1' : 'px-3 py-2'} rounded-lg font-semibold transition-all duration-200 backdrop-blur-sm border ${
                isMultiplayerEnabled
                  ? 'bg-orange-600/80 hover:bg-orange-500/80 border-orange-400/30 text-white'
                  : 'bg-gray-600/80 hover:bg-gray-500/80 border-gray-400/30 text-gray-300'
              }`}
              title={isMultiplayerEnabled ? 'Disconnect from multiplayer' : 'Connect to multiplayer'}
            >
              {isMobile ? '🌐' : `🌐 ${isMultiplayerEnabled ? 'Online' : 'Offline'}`}
            </button>

            {/* Player Count - Show when multiplayer is enabled */}
            {isMultiplayerEnabled && connectedPlayers > 0 && (
              <div className={`flex items-center gap-1 ${isMobile ? 'text-xs' : 'text-sm'} text-green-300 font-semibold`}>
                <span>👥</span>
                <span>{connectedPlayers} player{connectedPlayers !== 1 ? 's' : ''}</span>
              </div>
            )}

            {/* Home Button */}
            <a
              href="/"
              className="px-4 py-2 bg-red-600/80 hover:bg-red-500/80 text-white rounded-lg font-semibold transition-all duration-200 backdrop-blur-sm border border-red-400/30 flex items-center gap-2"
            >
              <span>🏠</span>
              {!isMobile && <span>Home</span>}
            </a>

            {/* Sound Button */}
            <button
              onClick={() => setShowMusicModal(true)}
              className={`px-4 py-2 rounded-lg font-semibold transition-all duration-200 backdrop-blur-sm border flex items-center gap-2 ${
                musicManager.isMusicEnabled
                  ? 'bg-green-600/80 hover:bg-green-500/80 border-green-400/30 text-white'
                  : 'bg-gray-600/80 hover:bg-gray-500/80 border-gray-400/30 text-gray-300'
              }`}
              title="Music settings"
            >
              <span>{musicManager.isMusicEnabled ? '🎵' : '🔇'}</span>
              {!isMobile && <span>Sound</span>}
            </button>

            {/* Controls Toggle Button - Only show on desktop */}
            {!isMobile && (
              <button
                onClick={() => setShowControls(!showControls)}
                className="px-4 py-2 bg-blue-600/80 hover:bg-blue-500/80 text-white rounded-lg font-semibold transition-all duration-200 backdrop-blur-sm border border-blue-400/30"
              >
                {showControls ? '✕ Hide Controls' : '? Controls'}
              </button>
            )}
          </div>
        </div>
      </div>
      )}

      {/* Game Canvas - Constrained width on desktop, centered with side fillers */}
      {/* Side filler areas for visual balance on desktop */}
      {!isMobile && viewportWidth > gameDimensions.width && (
        <>
          <div
            className="absolute inset-y-0 left-0 z-0 pointer-events-none"
            style={{
              width: `${Math.max(0, (viewportWidth - gameDimensions.width) / 2)}px`,
              background: 'linear-gradient(180deg, #f59e0b 0%, #f97316 45%, #ef4444 100%)',
              boxShadow: 'inset -10px 0 30px rgba(0,0,0,0.15), 0 0 24px rgba(0,0,0,0.08)',
              borderRight: '10px solid rgba(255,255,255,0.95)'
            }}
          />
          <div
            className="absolute inset-y-0 right-0 z-0 pointer-events-none"
            style={{
              width: `${Math.max(0, (viewportWidth - gameDimensions.width) / 2)}px`,
              background: 'linear-gradient(180deg, #f59e0b 0%, #f97316 45%, #ef4444 100%)',
              boxShadow: 'inset 10px 0 30px rgba(0,0,0,0.15), 0 0 24px rgba(0,0,0,0.08)',
              borderLeft: '10px solid rgba(255,255,255,0.95)'
            }}
          />

          {/* Desktop sidebar content containers */}
          <div className="absolute inset-y-0 left-0 z-10 flex flex-col items-end justify-center px-4">
            {/* Logo / Title card */}
            <div className="bg-white/95 rounded-3xl shadow-2xl border-8 border-white px-4 py-3 w-[180px] mb-4">
              <div className="text-center font-black tracking-wider" style={{
                background: 'linear-gradient(45deg, #60a5fa, #a78bfa)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>BRAIN CLIMB</div>
            </div>
            
            {/* Quiz Title - Desktop */}
            {quizTitle && (
              <div className="bg-blue-50/95 rounded-3xl shadow-2xl border-8 border-white px-4 py-3 w-[180px] mb-4">
                <div className="text-center">
                  <div className="text-xs font-semibold text-blue-600 mb-1">📚 Quiz</div>
                  <div className="text-sm font-bold text-gray-700 leading-tight">{quizTitle}</div>
                </div>
              </div>
            )}
            
            <div className="bg-white/92 rounded-3xl shadow-2xl border-8 border-white p-5 w-[180px] flex flex-col gap-5">
              {/* Energy */}
              <div className="flex items-center gap-3">
                <span className="text-2xl">⚡</span>
                <div className="flex-1">
                  <div className="h-3.5 bg-gray-200 rounded-full overflow-hidden">
                    <div className={`h-3.5 rounded-full transition-all duration-300 ${
                      gameStats.energy > 50 ? 'bg-green-500' : gameStats.energy > 25 ? 'bg-yellow-500' : 'bg-red-500'
                    }`} style={{ width: `${(gameStats.energy / gameStats.maxEnergy) * 100}%` }} />
                  </div>
                  <div className="text-xs text-gray-600 mt-1 font-mono">{gameStats.energy}/{gameStats.maxEnergy}</div>
                </div>
              </div>

              {/* Level */}
              <div className="grid grid-cols-1 gap-3">
                <div className="bg-white rounded-2xl border-4 border-white shadow px-3 py-2 flex items-center justify-between gap-2">
                  <span className="text-blue-600 font-semibold">Lv</span>
                  <span className="text-yellow-500 text-xl font-black font-mono drop-shadow">{gameStats.level}</span>
                </div>
                <div className="bg-white rounded-2xl border-4 border-white shadow px-3 py-2 flex items-center justify-between gap-2">
                  <span className="text-purple-600 font-semibold">Height</span>
                  <span className="text-gray-800 text-xl font-black font-mono drop-shadow">{gameStats.height}m</span>
                </div>
                <div className="bg-white rounded-2xl border-4 border-white shadow px-3 py-2 flex items-center justify-between gap-2">
                  <span className="text-yellow-500 text-xl">💎</span>
                  <span className="text-gray-800 text-xl font-black font-mono drop-shadow">{gameStats.gems || 0}</span>
                </div>
                <div className="bg-white rounded-2xl border-4 border-white shadow px-3 py-2 flex items-center justify-between gap-2">
                  <span className="text-yellow-600 text-xl">💰</span>
                  <span className="text-gray-800 text-xl font-black font-mono drop-shadow">{gameStats.coins || 0}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="absolute inset-y-0 right-0 z-10 flex flex-col items-start justify-center px-4">
            <div className="bg-white/92 rounded-3xl shadow-2xl border-8 border-white p-5 w-[190px] flex flex-col gap-5">


              {/* Multiplayer Toggle */}
              <button
                onClick={() => {
                  const scene = phaserGameRef.current?.scene?.scenes[0];
                  if (scene) {
                    if (isMultiplayerEnabled) {
                      scene.multiplayerManager?.disconnect();
                      setConnectedPlayers(0);
                    } else {
                      if (!scene.multiplayerManager) {
                        scene.multiplayerManager = new MultiplayerManager(scene);
                      }
                      scene.multiplayerManager.connect();
                    }
                  }
                  setIsMultiplayerEnabled(!isMultiplayerEnabled);
                }}
                className={`w-full px-4 py-3 rounded-2xl font-semibold transition-all duration-200 border-4 border-white shadow-xl ${
                  isMultiplayerEnabled ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-500'
                }`}
                title={isMultiplayerEnabled ? 'Disconnect from multiplayer' : 'Connect to multiplayer'}
              >
                {isMultiplayerEnabled ? (connectedPlayers > 0 ? `🌐 Online · ${connectedPlayers}` : '🌐 Online') : '🌐 Offline'}
              </button>

              {/* Controls Toggle */}
              <button
                onClick={() => setShowControls(!showControls)}
                className="w-full px-4 py-3 bg-blue-100 hover:bg-blue-200 text-blue-800 rounded-2xl font-semibold transition-all duration-200 border-4 border-white shadow-xl"
              >
                {showControls ? '✕ Hide Controls' : '? Controls'}
              </button>

              {/* Home Button */}
              <a
                href="/"
                className="w-full px-4 py-3 bg-red-100 hover:bg-red-200 text-red-800 rounded-2xl font-semibold transition-all duration-200 border-4 border-white shadow-xl text-center"
              >
                🏠 Home
              </a>

              {/* Sound Controls */}
              <div className={`w-full px-4 py-3 rounded-2xl font-semibold transition-all duration-200 border-4 border-white shadow-xl flex flex-col gap-2 ${
                musicManager.isMusicEnabled
                  ? 'bg-green-100 text-green-800'
                  : 'bg-gray-100 text-gray-800'
              }`}>
                {/* Sound Settings Row */}
                <button
                  onClick={() => setShowMusicModal(true)}
                  className="w-full flex items-center justify-center gap-2 py-1 rounded-lg bg-white/50 hover:bg-white/80 transition-all duration-200 text-sm font-bold"
                  title="Music settings"
                >
                  {musicManager.isMusicEnabled ? '🎵' : '🔇'} Sound
                </button>

                {/* Track Name Row */}
                <div className="text-xs text-center truncate opacity-75">
                  {musicManager.currentTrack?.name || 'Music'}
                </div>

                {/* Track Controls Row */}
                <div className="flex items-center justify-center gap-3">
                  <button
                    onClick={() => musicManager.previousTrack()}
                    className="px-3 py-1 rounded-lg bg-white/50 hover:bg-white/80 transition-all duration-200 text-sm font-bold"
                    title="Previous track"
                  >
                    ⏮️
                  </button>
                  <button
                    onClick={() => musicManager.nextTrack()}
                    className="px-3 py-1 rounded-lg bg-white/50 hover:bg-white/80 transition-all duration-200 text-sm font-bold"
                    title="Next track"
                  >
                    ⏭️
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      <div className="absolute inset-0 flex justify-center">
        <div
          ref={gameRef}
          className="h-full"
          style={{ width: `${gameDimensions.width}px` }}
        >
          {!isGameLoaded && (
            <div className="flex items-center justify-center h-full w-full bg-gradient-to-b from-blue-100 to-green-100">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading game...</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Touch Controls */}
      {isMobile && (
        <>
          {/* Touch fallback: hold a direction with one thumb, tap hop with the other. */}
          {!useTiltControls && (
            <div className="absolute bottom-4 right-4 left-4 z-20 flex items-end justify-between gap-4">
              <div className="flex gap-3">
                <button
                  onPointerDown={(e) => { e.preventDefault(); e.currentTarget.setPointerCapture?.(e.pointerId); setMobileDirection('left', true); }}
                  onPointerUp={(e) => { e.preventDefault(); setMobileDirection('left', false); }}
                  onPointerCancel={(e) => { e.preventDefault(); setMobileDirection('left', false); }}
                  onPointerLeave={(e) => { e.preventDefault(); setMobileDirection('left', false); }}
                  className="w-20 h-20 bg-slate-950/70 active:bg-blue-500/90 backdrop-blur-sm rounded-full border-2 border-white/40 flex flex-col items-center justify-center text-white font-black active:scale-95 transition-all duration-100 select-none shadow-xl"
                  style={{ touchAction: 'none' }}
                  aria-label="Move left"
                >
                  <span className="text-4xl leading-none">←</span>
                </button>
                <button
                  onPointerDown={(e) => { e.preventDefault(); e.currentTarget.setPointerCapture?.(e.pointerId); setMobileDirection('right', true); }}
                  onPointerUp={(e) => { e.preventDefault(); setMobileDirection('right', false); }}
                  onPointerCancel={(e) => { e.preventDefault(); setMobileDirection('right', false); }}
                  onPointerLeave={(e) => { e.preventDefault(); setMobileDirection('right', false); }}
                  className="w-20 h-20 bg-slate-950/70 active:bg-blue-500/90 backdrop-blur-sm rounded-full border-2 border-white/40 flex flex-col items-center justify-center text-white font-black active:scale-95 transition-all duration-100 select-none shadow-xl"
                  style={{ touchAction: 'none' }}
                  aria-label="Move right"
                >
                  <span className="text-4xl leading-none">→</span>
                </button>
              </div>
              <button
                onPointerDown={(e) => { e.preventDefault(); pulseMobileJump(); }}
                className="h-24 flex-1 max-w-[210px] bg-green-500/90 active:bg-green-400/95 backdrop-blur-sm rounded-[2rem] border-2 border-white/50 flex flex-col items-center justify-center text-white font-black active:scale-95 transition-all duration-100 select-none shadow-2xl"
                style={{ touchAction: 'none' }}
                aria-label="Hop"
              >
                <span className="text-3xl leading-none">🐸</span>
                <span className="text-2xl tracking-wide">HOP</span>
              </button>
            </div>
          )}

          {/* Tilt mode keeps one large hop target and uses phone tilt for movement. */}
          {useTiltControls && (
            <div className="absolute bottom-4 left-4 right-4 z-20 flex flex-col items-center gap-3">
              {tiltPermissionState === 'needs_permission' && (
                <button
                  onClick={handleEnableTiltControls}
                  className="w-full max-w-sm bg-purple-600/90 active:bg-purple-500/90 backdrop-blur-sm rounded-2xl border-2 border-white/40 px-5 py-3 text-white font-bold shadow-xl"
                >
                  Enable tilt movement
                </button>
              )}
              {tiltPermissionState === 'denied' && (
                <button
                  onClick={handleUseTouchControls}
                  className="w-full max-w-sm bg-slate-950/80 active:bg-slate-800/90 backdrop-blur-sm rounded-2xl border-2 border-white/40 px-5 py-3 text-white font-bold shadow-xl"
                >
                  Use touch controls
                </button>
              )}
              <button
                onPointerDown={(e) => { e.preventDefault(); handleTouchJump(); }}
                className="w-full max-w-sm bg-green-500/90 active:bg-green-400/95 backdrop-blur-sm rounded-[2rem] border-2 border-white/50 flex flex-col items-center justify-center text-white font-black active:scale-95 transition-all duration-100 select-none shadow-2xl"
                style={{
                  touchAction: 'none',
                  height: `${Math.min(window.innerHeight * 0.24, 180)}px`
                }}
                aria-label="Hop"
              >
                <span className="text-4xl mb-1">🐸</span>
                <span className="text-4xl tracking-wide">HOP</span>
                <span className="text-sm text-white/80 mt-1">Tilt phone to move</span>
              </button>
            </div>
          )}
        </>
      )}

      {/* Mobile Game Over Restart Button */}
      {isMobile && isGameOver && (
        <div className="absolute inset-0 z-30 flex items-center justify-center">
          <div className="bg-black/90 backdrop-blur-sm rounded-2xl p-6 border border-red-400/30 text-center">
            <h3 className="text-red-400 text-xl font-bold mb-4">Game Over!</h3>
            <p className="text-white mb-4">Height: {gameStats.height}m</p>
            {useTiltControls && (
              <p className="text-purple-300 text-sm mb-4">📱 Tilt mode was active</p>
            )}
            <button
              onClick={handleMobileRestart}
              className="px-6 py-3 bg-green-600/80 hover:bg-green-500/80 text-white rounded-xl font-bold text-lg transition-all duration-200 backdrop-blur-sm border border-green-400/30 active:scale-95"
            >
              🔄 Try Again
            </button>
          </div>
        </div>
      )}

      {/* Desktop Controls Help */}
      {!isMobile && showControls && (
        <div className="absolute bottom-4 right-4 z-10 animate-in slide-in-from-right-4 fade-in duration-300">
          <div className="bg-black/80 backdrop-blur-sm rounded-xl shadow-2xl p-4 text-sm border border-blue-400/30">
            <p className="text-blue-300 mb-3 font-bold text-base">Game Controls</p>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="flex gap-1">
                  <kbd className="px-2 py-1 bg-blue-600/50 text-white rounded text-xs font-mono">SPACE</kbd>
                  <kbd className="px-2 py-1 bg-blue-600/50 text-white rounded text-xs font-mono">W</kbd>
                  <kbd className="px-2 py-1 bg-blue-600/50 text-white rounded text-xs font-mono">↑</kbd>
                </div>
                <span className="text-gray-300">Hop</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex gap-1">
                  <kbd className="px-2 py-1 bg-blue-600/50 text-white rounded text-xs font-mono">A/D</kbd>
                  <kbd className="px-2 py-1 bg-blue-600/50 text-white rounded text-xs font-mono">←/→</kbd>
                </div>
                <span className="text-gray-300">Move</span>
              </div>
              <div className="flex items-center gap-2">
                <kbd className="px-2 py-1 bg-blue-600/50 text-white rounded text-xs font-mono">R</kbd>
                <span className="text-gray-300">Restart</span>
              </div>
            </div>
            <div className="mt-3 p-3 bg-gradient-to-r from-yellow-900/40 to-orange-900/40 rounded-lg border border-yellow-600/30">
              <div className="space-y-1 text-xs">
                <p className="text-yellow-300">🐸 <span className="font-semibold">Goal:</span> Reach new heights!</p>
                <p className="text-blue-300">⚡ <span className="font-semibold">Energy:</span> Each hop uses energy</p>
                <p className="text-purple-300">💫 <span className="font-semibold">Double hop</span> available!</p>
                <p className="text-yellow-300">💎 <span className="font-semibold">Gems:</span> Collect for rewards</p>
                <p className="text-green-300">🔊 <span className="font-semibold">Music:</span> Toggle with speaker icon</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Game Pause Overlay */}
      {gamesPaused && (
        <div className="absolute inset-0 bg-black bg-opacity-20 pointer-events-none z-20" />
      )}

      {/* Quiz Overlay */}
      <QuizOverlay
        isVisible={showQuiz}
        onCorrectAnswer={handleQuizSuccess}
        onWrongAnswer={handleQuizFailure}
        quizCode={quizCode}
        onQuizTitleChange={setQuizTitle}
      />

      {/* Level Complete Modal */}
      {showLevelComplete && (
        <>
          <style>{`
            @keyframes bounceIn {
              0% { transform: scale(0.3) rotate(-5deg); opacity: 0; }
              50% { transform: scale(1.1) rotate(2deg); opacity: 1; }
              70% { transform: scale(0.9) rotate(-1deg); }
              100% { transform: scale(1) rotate(0deg); opacity: 1; }
            }
          `}</style>
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div
            className="bg-gradient-to-br from-yellow-400 via-orange-400 to-red-500 rounded-3xl shadow-2xl max-w-md w-full transform"
            style={{
              animation: 'bounceIn 0.6s ease-out'
            }}
          >
            <div className="p-8 text-center">
              {/* Stars */}
              <div className="flex justify-center space-x-2 mb-6">
                <span className="text-6xl animate-pulse">⭐</span>
                <span className="text-6xl animate-pulse" style={{animationDelay: '0.2s'}}>⭐</span>
                <span className="text-6xl animate-pulse" style={{animationDelay: '0.4s'}}>⭐</span>
              </div>

              {/* Level Complete Text */}
              <h2 className="text-4xl font-bold text-white mb-2 drop-shadow-lg">
                🎉 Level Complete! 🎉
              </h2>
              <p className="text-xl text-white mb-6 drop-shadow">
                Amazing! You completed Level {currentLevel}!
              </p>

              {/* Next Level Button */}
              <button
                onClick={handleNextLevel}
                disabled={isTransitioning}
                className={`bg-green-500 hover:bg-green-600 text-white font-bold py-4 px-8 rounded-full text-xl shadow-lg transform hover:scale-105 transition-all duration-200 border-4 border-white ${
                  isTransitioning ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {isTransitioning ? '🔄 Loading...' : 'Next Level →'}
              </button>
            </div>
          </div>
          </div>
        </>
      )}
    </div>
  );
};

export default JumpingGame;
